/**
 * The shipped equipment presets, ordered branded all-in-one → modular US
 * electric (eBIAB, HERMS) → traditional → generic BIAB → commercial, which is
 * roughly the order a US homebrewer meets them.
 *
 * Every entry is `representative`. Makers publish vessel capacity, maximum
 * grain bill and element wattage; a few also publish a boil-off or a basket
 * deadspace. Published capacities and those rare rates are used verbatim
 * (`kettleVolumeL`, `mashTunVolumeL`, `nominalBatchVolumeL`); everything else
 * is the class-typical figure a brewer should start from and recalibrate. Each
 * preset's `notes` says which number to recalibrate first.
 *
 * Two conventions hold across the whole table:
 *
 * - **Boil shrinkage is 4 %** everywhere. It is a property of wort cooling from
 *   100 °C to 20 °C, not of the kettle.
 * - **Hop absorption is 6 mL/g** everywhere, kettle and dry hop alike. It is a
 *   property of the pellet, not the vessel; a brewer using a hop spider or a
 *   filter basket should lower it.
 *
 * The numbers that genuinely vary by system, and why:
 *
 * | | absorption | thickness | boil-off | mash eff |
 * | --- | --- | --- | --- | --- |
 * | All-in-one (grain basket, drained) | 0.8 L/kg | 2.7 L/kg | 2.0–6.0 L/h | 78–80 % |
 * | Recirculating eBIAB (bag, 240 V) | 0.8 L/kg | full volume | 4.5–5.5 L/h | 72–74 % |
 * | Steam condenser (lid-on, any maker) | (sibling) | (sibling) | ¼–¾ of lid-off | (sibling) |
 * | Electric HERMS (3-vessel) | 1.0 L/kg | 2.8 L/kg | 4.0–4.5 L/h | 78 % |
 * | Two-kettle continuous recirc (BrewEasy) | 1.0 L/kg | full volume | 2.8–5.0 L/h | 76 % |
 * | Brew-in-conical (mash tun + jacketed unitank) | 1.0 L/kg | 2.8 L/kg | 3.5–3.8 L/h | 75 % |
 * | Direct-fire three-tier (gas sculpture) | 1.0 L/kg | 2.8 L/kg | 4.0–9.0 L/h | 76–78 % |
 * | Cooler + kettle (batch sparge) | 1.0 L/kg | 2.8 L/kg | 3.0–4.0 L/h | 72–75 % |
 * | BIAB (bag, squeezed) | 0.8 L/kg | full volume | 3.0–3.5 L/h | 72 % |
 * | Stovetop BIAB (1–2.5 gal, kitchen pot) | 0.8 L/kg | full volume | 1.1–1.5 L/h | 72 % |
 * | Nano (1 bbl, fly sparge) | 1.0 L/kg | 2.8 L/kg | 8.0–9.0 L/h | 78–82 % |
 *
 * A recirculating all-in-one converts better than a static cooler mash (the
 * wort is pumped through the bed for the whole rest), and a full-volume BIAB
 * mash gets no rinse at all, which is the several points it gives away.
 * Evaporation scales with kettle *surface area*, not batch size — which is why
 * the 65 L Brewzilla loses only half again as much per hour as the 35 L, not
 * twice, and why a wide 20 gal 5500 W kettle can out-evaporate a taller 10 gal
 * urn. A few makers do publish a boil-off or a basket deadspace (Grainfather
 * lists both for the G70); those figures are used when they exist, and the
 * preset stays `representative` because the rest of the row is still class-typical.
 *
 * ## Deriving a boil-off from an element
 *
 * The surface-area rule above says which of two rigs evaporates faster. It does
 * not say *how much*, and for an electric vessel there is a hard ceiling that
 * does: every litre boiled off costs 2260 kJ, so an element can vaporise at most
 * about **1.59 L/h per kW**, whatever the kettle looks like. A 2000 W urn cannot
 * lose more than 3.2 L/h; a 5500 W kettle cannot lose more than 8.7 L/h.
 *
 * Real rigs land at 60–80 % of that ceiling, because part of the element is
 * paying for the vessel's standing loss and because nobody boils at 100 % power
 * for sixty minutes. The three published Grainfather figures are the calibration:
 * the S40's 3 L/h is 82 % of its 2300 W ceiling, the G40's 3 L/h at 15 A is
 * 60–70 % of its, and the G70's 4 L/h is in the same band. So every derived
 * electric boil-off below is *element wattage × 1.59 × (0.6–0.8)*, nudged within
 * that window by bore diameter — wider bore, more standing loss, lower fraction.
 * Two consequences worth stating because they surprise people: fitting the same
 * element to a bigger vessel (Bräu Supply put 5500 W in both the 10 and the 20
 * gallon) barely moves the rate, and a wide low-wattage vessel (the Braumeister's
 * 2000 W across a 40 cm bore) evaporates far less than its diameter suggests.
 * Direct-fire gas rows have no wattage to work from, so those are scaled off the
 * `generic-cooler-10-gal` propane row by kettle bore area instead.
 *
 * ## Deadspace vs. loss
 *
 * The two mash-tun figures are not interchangeable, and the split runs straight
 * down the middle of this table:
 *
 * - **`mashTunDeadspaceL`** is the volume sitting under a grain basket or malt
 *   pipe *inside the boil kettle*. Every single-vessel machine here — every
 *   Grainfather, Foundry, BrewZilla, Solo, Clawhammer — has one, and on those
 *   systems it is not lost: the wort under the basket is in the kettle already.
 *   A maker's published basket volume therefore belongs here, where it costs no
 *   water. Grainfather's 6.8 L for the G70 is the biggest, and putting it in the
 *   loss column used to inflate that rig's strike water by nearly two gallons.
 * - **`mashTunLossL`** is wort left in a *separate* mash tun that never reaches
 *   the kettle — the cooler, the HERMS tun, the nano's lauter tun. That volume
 *   really is gone, so the brewer has to measure out extra water for it.
 *
 * Full-volume BIAB rows are zero on both: the bag hangs in the kettle and the
 * grain absorption figure already accounts for what the bag keeps.
 */

import type { EquipmentPreset } from '../../schema/equipment';

/** Fields every preset shares, so each entry below is only what differs. */
const SHARED = {
  boilShrinkagePercent: 4,
  hopAbsorptionMlPerG: 6,
  dryHopAbsorptionMlPerG: 6,
  topUpKettleL: 0,
  topUpFermenterL: 0,
  mashTunThermalMassLEquivalent: 0,
  mashTunInitialTempC: 20,
  provenance: 'representative',
} as const;

/** What a condenser sibling is allowed to change about its open-boil row. */
interface CondenserSpec {
  readonly id: EquipmentPreset['id'];
  readonly name: EquipmentPreset['name'];
  readonly description: EquipmentPreset['description'];
  readonly notes: EquipmentPreset['notes'];
  readonly boilOffRateLPerHour: EquipmentPreset['boilOffRateLPerHour'];
}

/**
 * A lid-on steam condenser sibling of an open-boil row.
 *
 * A spray condenser on the kettle's lid port pulls the steam into a stream of
 * tap water, and every maker's instruction is the same: turn the element down
 * — half power or less — and expect far less evaporation. The sibling keeps
 * the open row's vessels, losses and efficiency, so the two rows cannot drift;
 * only identity, prose, tags and boil-off change. How much boil-off drops is
 * the maker's call, and each sibling's `notes` says whose number it is:
 * Spike and Anvil publish a half; Bräu Supply a quarter to a third; Ruby Street
 * three quarters on their 1 bbl; KegLand and Brewtools publish nothing, so
 * those rows carry the half-rate convention and say so.
 */
function withCondenser(base: EquipmentPreset, spec: CondenserSpec): EquipmentPreset {
  return {
    ...base,
    ...spec,
    tags: [...base.tags, 'steam-condenser'],
  };
}

/**
 * Brew Hardware's Steam Slayer, the condenser most of the others were built to
 * match. They tell you to run about half the element power and plan for about
 * half the evaporation (their shop kettle: 1.5 gal/h lid-off → 0.75 gal/h with
 * the Slayer). Tagged by product name as well, because that is the name
 * brewers search for.
 */
function withSteamSlayer(base: EquipmentPreset, spec: CondenserSpec): EquipmentPreset {
  const sibling = withCondenser(base, spec);
  return { ...sibling, tags: [...sibling.tags, 'steam-slayer'] };
}

/**
 * Clawhammer sell one 10.5 gal kettle on two elements — 120 V / 1650 W and
 * 240 V / 5500 W — as two separate SKUs. Both product pages publish the same
 * vessel (10.5 gal, 14.5 in diameter, 17 in tall); only the element changes, so
 * the 240 V row is derived from the 120 V one and only evaporation moves. Neither
 * page publishes a boil-off rate.
 */
const clawhammer10v120: EquipmentPreset = {
  ...SHARED,
  id: 'clawhammer-10-gal',
  name: 'Clawhammer 10 gal 120 V',
  maker: 'Clawhammer Supply',
  description:
    'A 10.5 gallon modular electric system with a self-draining stainless grain basket, an external pump and a 1650 W element on a standard 120 V circuit. Built for a 5 gallon batch — the common US 5 gallon keg fill.',
  notes:
    'The 120 V element needs the lid cracked (and often a jacket) to hold a rolling boil, which is why evaporation sits near 0.6 gal/h rather than a 240 V figure. Recalibrate boil-off first; drop mash efficiency a few points on grain bills above about 15 lb, when the basket gets tight. Pick the 240 V sibling if you brew on a dryer circuit.',
  nominalBatchVolumeL: 19,
  tags: ['all-in-one', 'electric', 'recirculating', 'malt-pipe'],
  spargeType: 'batch',
  mashTunDeadspaceL: 1.2,
  mashTunLossL: 0,
  grainAbsorptionLPerKg: 0.8,
  mashThicknessLPerKg: 2.8,
  mashStandingLossCPerHour: 0,
  boilOffRateLPerHour: 2.2,
  kettleDeadspaceL: 1.0,
  chillerLossL: 0.5,
  fermenterTrubLossL: 1.0,
  mashEfficiencyPercent: 78,
  brewhouseEfficiencyPercent: 71,
  kettleVolumeL: 39.7,
  mashTunVolumeL: 39.7,
};

const brewHardwareEbiab10: EquipmentPreset = {
  ...SHARED,
  id: 'brew-hardware-ebiab-10',
  name: 'Brew Hardware eBIAB 10 gal',
  maker: 'Brew Hardware',
  description:
    "Brew Hardware's recirculating electric BIAB on a 10 gallon Spike kettle: 5500 W element, mesh bag over a coarse false bottom, and the whirlpool-plus-recirc manifold. Their 5–6 gallon system for low-to-moderate gravity beers — they cap it around 8% ABV because the kettle fills up.",
  notes:
    'Full-volume no-sparge in a 10 gal kettle is tight: 7–8 gallons of strike plus grain leaves little headspace, which is the gravity ceiling. Recalibrate boil-off first — 5500 W lid-off in a 10 gal Spike sits near 1.1 gal/h. Pick the Steam Slayer sibling if you boil with the lid on. Move to the 15 gal if you regularly brew above about 1.070.',
  nominalBatchVolumeL: 19,
  tags: ['biab', 'electric', 'recirculating', 'full-volume'],
  spargeType: 'biab-full-volume',
  mashTunDeadspaceL: 0,
  mashTunLossL: 0,
  grainAbsorptionLPerKg: 0.8,
  mashThicknessLPerKg: 5.5,
  mashStandingLossCPerHour: 0,
  boilOffRateLPerHour: 4.0,
  kettleDeadspaceL: 1.0,
  chillerLossL: 0.4,
  fermenterTrubLossL: 1.0,
  mashEfficiencyPercent: 74,
  brewhouseEfficiencyPercent: 68,
  kettleVolumeL: 38,
  mashTunVolumeL: 38,
};

const brewHardwareEbiab15: EquipmentPreset = {
  ...SHARED,
  id: 'brew-hardware-ebiab-15',
  name: 'Brew Hardware eBIAB 15 gal',
  maker: 'Brew Hardware',
  description:
    "Brew Hardware's recirculating electric BIAB on a 15 gallon Spike kettle: 5500 W element, mesh bag over a coarse false bottom, and a whirlpool-plus-recirc manifold that stirs the heated zone while wort returns over the grain. Their recommended 5–6 gallon everyday system.",
  notes:
    'Full-volume no-sparge, so mash efficiency starts a few points below a basket-and-sparge urn. They size this kettle for 8–9 gallons of strike to land about 5.5 gallons in the fermenter. Recalibrate boil-off — 5500 W lid-off in a 15 gal Spike is a fast boil. Pick the Steam Slayer sibling if you boil with the lid on.',
  nominalBatchVolumeL: 19,
  tags: ['biab', 'electric', 'recirculating', 'full-volume'],
  spargeType: 'biab-full-volume',
  mashTunDeadspaceL: 0,
  mashTunLossL: 0,
  grainAbsorptionLPerKg: 0.8,
  mashThicknessLPerKg: 6.0,
  mashStandingLossCPerHour: 0,
  boilOffRateLPerHour: 4.5,
  kettleDeadspaceL: 1.0,
  chillerLossL: 0.4,
  fermenterTrubLossL: 1.0,
  mashEfficiencyPercent: 74,
  brewhouseEfficiencyPercent: 68,
  kettleVolumeL: 57,
  mashTunVolumeL: 57,
};

const brewHardwareEbiab20: EquipmentPreset = {
  ...SHARED,
  id: 'brew-hardware-ebiab-20',
  name: 'Brew Hardware eBIAB 20 gal',
  maker: 'Brew Hardware',
  description:
    'The same recirculating bag-and-manifold BIAB on a 20 gallon Spike kettle, sized for a 10 gallon batch — 6 to 12 gallons with no real gravity ceiling. Same 5500 W element, pump and controller as the 15 gal; only the kettle and bag change.',
  notes:
    'They quote 14–15 gallons of strike for an 11 gallon batch; a 10 gallon fermenter fill is a bit less. Evaporation is higher than the 15 gal because the kettle is wider, not because the element is. A squeezed bag still gives water back — keep absorption at 0.8 unless you do not squeeze. Pick the Steam Slayer sibling if you boil with the lid on.',
  nominalBatchVolumeL: 38,
  tags: ['biab', 'electric', 'recirculating', 'full-volume', 'double-batch'],
  spargeType: 'biab-full-volume',
  mashTunDeadspaceL: 0,
  mashTunLossL: 0,
  grainAbsorptionLPerKg: 0.8,
  mashThicknessLPerKg: 5.0,
  mashStandingLossCPerHour: 0,
  boilOffRateLPerHour: 5.5,
  kettleDeadspaceL: 1.5,
  chillerLossL: 0.5,
  fermenterTrubLossL: 2.0,
  mashEfficiencyPercent: 74,
  brewhouseEfficiencyPercent: 68,
  kettleVolumeL: 76,
  mashTunVolumeL: 76,
};

const brewHardwareHerms15: EquipmentPreset = {
  ...SHARED,
  id: 'brew-hardware-herms-15',
  name: 'Brew Hardware 15 gal HERMS',
  maker: 'Brew Hardware',
  description:
    'The typical Brew Hardware three-vessel electric HERMS: 15 gallon mash tun and hot-liquor tank, a 15 gallon boil kettle, a HERMS coil in the HLT and a Brew Buddy-style 240 V panel. Built around a 10 gallon / 10 gallon batch.',
  notes:
    'This is the class-typical 15 gal vessel build, not a single SKU — kettle sizes and coil length vary. Recirculating mash plus a sparge is why efficiency sits above their eBIAB. Recalibrate boil-off for your kettle diameter. Pick the Steam Slayer sibling if the boil kettle runs lid-on.',
  nominalBatchVolumeL: 38,
  tags: ['three-vessel', 'electric', 'herms', 'fly-sparge'],
  spargeType: 'fly',
  mashTunDeadspaceL: 0,
  mashTunLossL: 1.5,
  grainAbsorptionLPerKg: 1.0,
  mashThicknessLPerKg: 2.8,
  mashStandingLossCPerHour: 0,
  boilOffRateLPerHour: 4.0,
  kettleDeadspaceL: 1.5,
  chillerLossL: 0.8,
  fermenterTrubLossL: 2.0,
  mashEfficiencyPercent: 78,
  brewhouseEfficiencyPercent: 72,
  kettleVolumeL: 57,
  mashTunVolumeL: 57,
};

/** Every equipment preset. */
/**
 * The shelf as authored: every open-boil row, with Brew Hardware's Steam Slayer
 * siblings beside their rows because those bases are named constants. The
 * other makers' condenser siblings are folded in below.
 */
const SHELF: readonly EquipmentPreset[] = [
  {
    ...SHARED,
    id: 'grainfather-g30',
    name: 'Grainfather G30',
    maker: 'Grainfather',
    description:
      'The 8 gallon recirculating all-in-one that made single-vessel electric brewing mainstream. A pump circulates wort through the grain basket for the whole mash, then the basket lifts out and drains over the kettle for a top-up sparge.',
    notes:
      'Recalibrate the boil-off rate first — it swings between 0.5 and 0.9 gal/h depending on how far the lid is left open. Mash efficiency is high because the bed is recirculated for the full rest; drop it a few points if you skip the basket sparge.',
    nominalBatchVolumeL: 23,
    tags: ['all-in-one', 'electric', 'recirculating'],
    spargeType: 'batch',
    mashTunDeadspaceL: 1.5,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 2.7,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 2.5,
    kettleDeadspaceL: 1.0,
    chillerLossL: 0.5,
    fermenterTrubLossL: 1.0,
    mashEfficiencyPercent: 80,
    brewhouseEfficiencyPercent: 73,
    kettleVolumeL: 30,
    mashTunVolumeL: 30,
  },
  {
    ...SHARED,
    id: 'grainfather-g40',
    name: 'Grainfather G40',
    maker: 'Grainfather',
    description:
      'The 10.5 gallon G40, with a wider kettle and a larger grain basket for batches up to about 8 gallons. Same recirculating mash, same basket sparge, more surface area over the element.',
    notes:
      'The wider vessel evaporates faster than the G30 for the same boil — budget around 0.8 gal/h and measure it on your first batch. Everything else behaves like its smaller sibling.',
    nominalBatchVolumeL: 30,
    tags: ['all-in-one', 'electric', 'recirculating'],
    spargeType: 'batch',
    mashTunDeadspaceL: 2.0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 2.7,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 3.0,
    kettleDeadspaceL: 1.5,
    chillerLossL: 0.5,
    fermenterTrubLossL: 1.5,
    mashEfficiencyPercent: 80,
    brewhouseEfficiencyPercent: 73,
    kettleVolumeL: 40,
    mashTunVolumeL: 40,
  },
  {
    ...SHARED,
    id: 'grainfather-g70',
    name: 'Grainfather G70',
    maker: 'Grainfather',
    description:
      'The 18.5 gallon Grainfather: same recirculating basket and app-controlled mash as the G30/G40, sized for a comfortable double batch or a 13 gallon transfer. The US/CA unit is 220–240 V only.',
    notes:
      'Grainfather publishes 1.1 gal/h boil-off and 1.8 gal of mash-tun deadspace for this model — both are used here. That 1.8 gal sits under the grain basket, inside the kettle, so it comes back to the boil: it is recorded as deadspace and costs you no extra water. Recalibrate boil-off if you run a steam condenser or leave the lid further open.',
    nominalBatchVolumeL: 50,
    tags: ['all-in-one', 'electric', 'recirculating', 'double-batch'],
    spargeType: 'batch',
    mashTunDeadspaceL: 6.8,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 2.7,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 4.0,
    kettleDeadspaceL: 2.5,
    chillerLossL: 0.8,
    fermenterTrubLossL: 2.5,
    mashEfficiencyPercent: 80,
    brewhouseEfficiencyPercent: 70,
    kettleVolumeL: 70,
    mashTunVolumeL: 70,
  },
  /**
   * The S40 is the analogue sibling of the G40 and the most generously
   * documented rig in this table: Grainfather's own spec sheet
   * (help.grainfather.com/hc/en-us/articles/4408048727057-Technical-Specifications-S40)
   * publishes the boil-off (3 L/h) *and* the mash-tun deadspace (7 L) *and* the
   * batch and grain limits. Only efficiency is ours. The G40 sheet
   * (…/4408049220241-Technical-Specifications-G40) is what the differences below
   * are read against: same 46 L max pre-boil and same 40 L max batch, a smaller
   * 2300 W element instead of 3150 W, 0.6 mm steel instead of 0.8, 10 kg of grain
   * instead of 13, an immersion chiller instead of a counterflow, and no app.
   */
  {
    ...SHARED,
    id: 'grainfather-s40',
    name: 'Grainfather S40',
    maker: 'Grainfather',
    description:
      'The analogue S40: the same 40 L recirculating boiler and grain basket as the G40, with a knob instead of an app, a 2300 W element instead of 3150 W and an immersion chiller instead of a counterflow. Grainfather rate it for 10 to 40 L batches on a 5 to 10 kg grain bill.',
    notes:
      'Both published figures are used verbatim: 3 L/h boil-off and 7 L of mash-tun deadspace. That 7 L sits under the basket inside the boiler, so it comes back to the boil and costs no water. Chiller loss is only 0.2 L because the S40 ships an immersion chiller that leaves the wort in the kettle — the G40 row carries 0.5 L for the counterflow and its hoses. Recalibrate mash efficiency, the one number Grainfather do not publish.',
    nominalBatchVolumeL: 30,
    tags: ['all-in-one', 'electric', 'recirculating'],
    spargeType: 'batch',
    mashTunDeadspaceL: 7.0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 2.7,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 3.0,
    kettleDeadspaceL: 1.5,
    chillerLossL: 0.2,
    fermenterTrubLossL: 1.5,
    mashEfficiencyPercent: 80,
    brewhouseEfficiencyPercent: 73,
    kettleVolumeL: 40,
    mashTunVolumeL: 40,
  },
  {
    ...SHARED,
    id: 'anvil-foundry-6-5',
    name: 'Anvil Foundry 6.5 gal',
    maker: 'Anvil Brewing Equipment',
    description:
      'A 6.5 gallon electric all-in-one with a removable malt pipe, built for 5 gallon batches. The optional recirculation pump is what separates it from a very good electric BIAB kettle.',
    notes:
      'Anvil recommends brewing with the lid mostly on, which keeps evaporation near 0.5 gal/h — noticeably lower than an open kettle. If you boil uncovered, expect to roughly double it.',
    nominalBatchVolumeL: 19,
    tags: ['all-in-one', 'electric', 'malt-pipe'],
    spargeType: 'batch',
    mashTunDeadspaceL: 1.0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 2.0,
    kettleDeadspaceL: 0.9,
    chillerLossL: 0.3,
    fermenterTrubLossL: 1.0,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 71,
    kettleVolumeL: 24.6,
    mashTunVolumeL: 24.6,
  },
  {
    ...SHARED,
    id: 'anvil-foundry-10-5',
    name: 'Anvil Foundry 10.5 gal',
    maker: 'Anvil Brewing Equipment',
    description:
      'The 10.5 gallon Foundry: the same malt pipe and controller scaled up to a 10 gallon batch, or a comfortable 6 gallon batch with headroom for a big grain bill.',
    notes:
      'Run it at 6 gallons and the extra headroom means no boil-overs and a full-size grain bill; run it at 10 and check the malt pipe capacity before designing a high-gravity beer.',
    nominalBatchVolumeL: 23,
    tags: ['all-in-one', 'electric', 'malt-pipe'],
    spargeType: 'batch',
    mashTunDeadspaceL: 1.5,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 2.8,
    kettleDeadspaceL: 1.2,
    chillerLossL: 0.4,
    fermenterTrubLossL: 1.2,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 71,
    kettleVolumeL: 39.7,
    mashTunVolumeL: 39.7,
  },
  {
    ...SHARED,
    id: 'anvil-foundry-18',
    name: 'Anvil Foundry 18 gal',
    maker: 'Anvil Brewing Equipment',
    description:
      'The 18 gallon Foundry, 240 V and 4000 W, built for a 10 gallon batch. Same malt pipe and optional recirculation pump as the smaller Foundries, with a 36 lb grain basket and enough headroom for a 10 gallon no-sparge mash.',
    notes:
      'Anvil rates this one at 1–2 gal/h of boil-off depending on power — start at 1.2 gal/h for a rolling boil and measure it. Unlike the 6.5 and 10.5, the 18 gal manual says to boil with the lid off; a steam condenser is the way to cut evaporation if you need to.',
    nominalBatchVolumeL: 38,
    tags: ['all-in-one', 'electric', 'malt-pipe', 'double-batch'],
    spargeType: 'batch',
    mashTunDeadspaceL: 2.0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 4.5,
    kettleDeadspaceL: 1.8,
    chillerLossL: 0.6,
    fermenterTrubLossL: 2.0,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 71,
    kettleVolumeL: 68.1,
    mashTunVolumeL: 68.1,
  },
  /**
   * The cheapest all-in-one in this table, and the only one built around a
   * 110 V kitchen circuit rather than a dryer outlet. Brewer's Edge publish two
   * figures for it in their FAQ (brewersedge.com/faq) and both are used here:
   * about half a gallon an hour of evaporation, and about half a gallon of wort
   * below the valve. The Series 2 with the recirculation pump is the same
   * vessel on the same losses — only mash thickness moves, because wort pumped
   * through the bed converts evenly at a thickness a still mash would not.
   */
  {
    ...SHARED,
    id: 'brewers-edge-mash-and-boil',
    name: "Brewer's Edge Mash & Boil",
    maker: "Brewer's Edge",
    description:
      'A 7.5 gallon 110 V all-in-one with a sparging basket, built for 5 gallon batches on an ordinary kitchen circuit. No pump: the mash sits still, and a stir or a sparge is how the wort moves.',
    notes:
      "Brewer's Edge puts the boil-off at about half a gallon an hour, and the deadspace below the valve at about half a gallon, both of which we start from here. Sixteen pounds of grain is the basket's limit, and three gallons is the least water the element wants under it. It heats on 1600 W, so allow most of an hour from cold to a boil.",
    nominalBatchVolumeL: 19,
    tags: ['all-in-one', 'electric', 'malt-pipe'],
    spargeType: 'batch',
    mashTunDeadspaceL: 0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 3.0,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 2.1,
    kettleDeadspaceL: 1.9,
    chillerLossL: 0.3,
    fermenterTrubLossL: 1.0,
    mashEfficiencyPercent: 72,
    brewhouseEfficiencyPercent: 65,
    kettleVolumeL: 28.4,
    mashTunVolumeL: 28.4,
  },
  {
    ...SHARED,
    id: 'brewers-edge-mash-and-boil-series-2-pump',
    name: "Brewer's Edge Mash & Boil Series 2 + pump",
    maker: "Brewer's Edge",
    description:
      'The Series 2 with the recirculation pump: the same 7.5 gallon 110 V kettle and basket, with the wort moving through the grain during the mash and a controller that holds to about a degree and a half.',
    notes:
      "Same vessel and the same losses as the original, so the volumes here are the maker's own. Recirculation lets a slightly thicker mash convert evenly; if you run it thin the pump has more to move and the sparge does less.",
    nominalBatchVolumeL: 19,
    tags: ['all-in-one', 'electric', 'recirculating', 'malt-pipe'],
    spargeType: 'batch',
    mashTunDeadspaceL: 0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 2.1,
    kettleDeadspaceL: 1.9,
    chillerLossL: 0.3,
    fermenterTrubLossL: 1.0,
    mashEfficiencyPercent: 72,
    brewhouseEfficiencyPercent: 65,
    kettleVolumeL: 28.4,
    mashTunVolumeL: 28.4,
  },
  {
    ...SHARED,
    id: 'brewzilla-gen4-35',
    name: 'BrewZilla Gen 4 35 L',
    maker: 'KegLand',
    description:
      'A 9 gallon recirculating all-in-one with a stainless malt pipe and a built-in counterflow-ready pump. The generation-4 revision added a wider element plate and a taller pipe for bigger grain bills.',
    notes:
      'Start at 0.8 gal/h boil-off with the lid off and measure it — the wide flat body evaporates faster than a tall narrow kettle of the same volume. The pump deadspace is real; do not set the kettle deadspace to zero.',
    nominalBatchVolumeL: 23,
    tags: ['all-in-one', 'electric', 'recirculating'],
    spargeType: 'batch',
    mashTunDeadspaceL: 1.5,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 2.7,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 3.0,
    kettleDeadspaceL: 1.2,
    chillerLossL: 0.5,
    fermenterTrubLossL: 1.0,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 71,
    kettleVolumeL: 35,
    mashTunVolumeL: 35,
  },
  {
    ...SHARED,
    id: 'brewzilla-gen4-65',
    name: 'BrewZilla Gen 4 65 L',
    maker: 'KegLand',
    description:
      'The 17 gallon BrewZilla, built around a 12 gallon batch — double-batch homebrewing or a very small commercial pilot. Two elements, the same malt pipe design as the 9 gallon.',
    notes:
      'Evaporation scales with surface area rather than volume, so this loses about half again as much per hour as the 9 gallon, not twice as much. Fixed losses are proportionally smaller at this batch size.',
    nominalBatchVolumeL: 45,
    tags: ['all-in-one', 'electric', 'recirculating', 'double-batch'],
    spargeType: 'batch',
    mashTunDeadspaceL: 2.5,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 2.7,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 4.5,
    kettleDeadspaceL: 2.0,
    chillerLossL: 0.8,
    fermenterTrubLossL: 2.0,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 71,
    kettleVolumeL: 65,
    mashTunVolumeL: 65,
  },
  /**
   * KegLand publish the 100 L Gen 4's vessel numbers and nothing else
   * (kegland.com.au/products/brewzilla-100l-gen-4): 107 L brimful boiler, 72 L
   * malt pipe, batches "up to 90 L", three 2200 W ultra-low-watt-density
   * elements on a 32 A single-phase supply, 52 cm boiler diameter, 70 cm tall.
   * No boil-off, no efficiency, no maximum grain bill — a 40 lb figure that
   * circulates is retailer copy, not KegLand's, so it is not used here. The
   * deadspace is scaled off the 35 and 65 L rows rather than derived, because
   * KegLand publish the malt pipe's own volume but not the gap beneath it.
   */
  {
    ...SHARED,
    id: 'brewzilla-gen4-100',
    name: 'BrewZilla Gen 4 100 L',
    maker: 'KegLand',
    description:
      'The 28 gallon BrewZilla: a 107 L boiler, a 72 L malt pipe and three 2200 W elements on a 32 A supply, built for batches up to 90 L. The size where a BrewZilla stops being a homebrew urn and starts being a pilot vessel — KegLand publish a three-phase rewire for it.',
    notes:
      "Boil-off is derived, not published: 6.6 kW gives a 10.5 L/h ceiling and a 52 cm bore spends a good share of that on standing loss, which puts a rolling boil near 6 L/h. That is about a third more per hour than the 65 L for half again the volume, exactly the surface-area rule. Recalibrate it first, then mash efficiency on grain bills big enough to pack the pipe. The nominal batch is set at 75 L rather than KegLand's 90 L maximum so pre-boil is not kissing the rim.",
    nominalBatchVolumeL: 75,
    tags: ['all-in-one', 'electric', 'recirculating', 'double-batch'],
    spargeType: 'batch',
    mashTunDeadspaceL: 3.5,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 2.7,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 6.0,
    kettleDeadspaceL: 3.0,
    chillerLossL: 1.2,
    fermenterTrubLossL: 3.5,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 72,
    kettleVolumeL: 107,
    mashTunVolumeL: 107,
  },
  /**
   * Brewtools' pro series, from docs.brewtools.com/brewing/pro-series-brewing-system.
   * They are the best-documented vessels in this file — tank volume, tank bore and
   * height, malt pipe volume, malt pipe bore and height, minimum liquid volume,
   * element count and watt density are all published for all three — and they are
   * **end of life**: brewtools.com/products/brewing-systems/previous-models states
   * they "were launched in 2017 and was in production until the end of 2025",
   * superseded by the B25/B50/B100/B180. They are here because the published spec
   * is complete and eight years of them are in brewers' garages.
   *
   * The deadspace figures are the one place this file gets to *derive* a basket
   * volume from geometry instead of guessing it. Brewtools publish both heights:
   * the B40pro's tank is 36 cm deep and its malt pipe 32 cm, so 4 cm of the 40 cm
   * bore sits beneath the pipe — π·20²·4 ≈ 5.0 L. The same subtraction gives 7.9 L
   * on the B80pro (Ø50, 46 − 42 cm) and 14.1 L on the B150pro (Ø60, 60 − 55 cm).
   * As a sanity check those land at 8–11 % of tank volume, and Grainfather's
   * *published* G70 basket volume is 9.7 % of its kettle.
   *
   * Batch volumes are not published either. Brewtools' naming rule is that "the
   * model name tells you the maximum recommended boil volume", so each row starts
   * from that maximum, takes off an hour of boil-off, 4 % shrinkage and the kettle
   * and chiller losses, and then backs off to a round number the brewer will
   * actually use rather than the ceiling.
   */
  {
    ...SHARED,
    id: 'brewtools-b40pro',
    name: 'Brewtools B40pro',
    maker: 'Brewtools',
    description:
      'A 46 L Swedish all-in-one with a bottom-drain valve block, a 25.7 L malt pipe and a 25 L/min recirculation pump — the high-flow end of the malt-pipe class. Two 1600 W elements on a single 230 V circuit, 9 kg of grain, 40 L of recommended maximum boil.',
    notes:
      "Discontinued at the end of 2025 in favour of the B25/B50; the published spec is complete, so the row stands. Deadspace is derived from Brewtools' own geometry — 4 cm of the 40 cm bore sits below the malt pipe — and it is inside the kettle, so it costs no water. Boil-off is derived: 3200 W caps evaporation at 5.1 L/h and a rolling boil runs about 70 % of that. The bottom drain is why kettle deadspace is under a liter where a side-pickup kettle carries 1.5.",
    nominalBatchVolumeL: 30,
    tags: ['all-in-one', 'electric', 'recirculating', 'malt-pipe'],
    spargeType: 'batch',
    mashTunDeadspaceL: 5.0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 2.7,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 3.5,
    kettleDeadspaceL: 0.8,
    chillerLossL: 0.5,
    fermenterTrubLossL: 1.5,
    mashEfficiencyPercent: 80,
    brewhouseEfficiencyPercent: 73,
    kettleVolumeL: 46,
    mashTunVolumeL: 46,
  },
  {
    ...SHARED,
    id: 'brewtools-b80pro',
    name: 'Brewtools B80pro',
    maker: 'Brewtools',
    description:
      'The 90 L B80pro: a 58 L malt pipe, 20 kg of grain and two 3000 W elements on two separate circuits, with the same bottom-drain block and high-flow pump as the B40pro. Brewtools recommend a maximum 80 L boil, which is a comfortable double batch.',
    notes:
      'Also end-of-life since 2025. The 7.9 L under the malt pipe is derived the same way as the B40pro — 4 cm of a 50 cm bore — and returns to the boil. 6000 W caps evaporation at 9.6 L/h; a rolling boil in a 50 cm bore runs nearer 5.5. Fixed losses are a much smaller share of a 60 L batch than of a 30 L one, which is where the extra point of brewhouse efficiency comes from. Recalibrate boil-off first; it moves most with how far the lid is left open.',
    nominalBatchVolumeL: 60,
    tags: ['all-in-one', 'electric', 'recirculating', 'malt-pipe', 'double-batch'],
    spargeType: 'batch',
    mashTunDeadspaceL: 7.9,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 2.7,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 5.5,
    kettleDeadspaceL: 1.2,
    chillerLossL: 0.8,
    fermenterTrubLossL: 2.5,
    mashEfficiencyPercent: 80,
    brewhouseEfficiencyPercent: 74,
    kettleVolumeL: 90,
    mashTunVolumeL: 90,
  },
  {
    ...SHARED,
    id: 'brewtools-b150pro',
    name: 'Brewtools B150pro',
    maker: 'Brewtools',
    description:
      'The 169 L top of the pro range: a 108 L malt pipe, 35 kg of grain, two 3300 W elements on separate circuits and a 150 L recommended maximum boil. A pilot vessel in an all-in-one shape, and the only rig here that needs its element at close to full power to hold a boil.',
    notes:
      'Discontinued end of 2025. The 14.1 L beneath the malt pipe is derived from the published heights (60 cm tank, 55 cm pipe, 60 cm bore) and stays in the kettle. Boil-off is the one number where this rig breaks the pattern: 6600 W is only a tenth more element than the B80pro spread over 44 % more surface, so it barely out-evaporates its smaller sibling per hour while boiling nearly twice the wort — 6 L/h, and near full power to get it. Recalibrate that before anything else.',
    nominalBatchVolumeL: 120,
    tags: ['all-in-one', 'electric', 'recirculating', 'malt-pipe', 'double-batch'],
    spargeType: 'batch',
    mashTunDeadspaceL: 14.1,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 2.7,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 6.0,
    kettleDeadspaceL: 2.0,
    chillerLossL: 1.5,
    fermenterTrubLossL: 5.0,
    mashEfficiencyPercent: 80,
    brewhouseEfficiencyPercent: 75,
    kettleVolumeL: 169,
    mashTunVolumeL: 169,
  },
  /**
   * The Braumeister, from Speidel's shop pages
   * (shop.speidels-braumeister.de/en/20-litre-braumeister/20-litre-braumeister-plus1
   * and .../50-litre-braumeister/50-litre-braumeister-plus1) and the technical data
   * section of the shared 10/20/50/100 L manual. It is the oldest malt-pipe
   * all-in-one still in production and the one this table was overdue.
   *
   * Two things make it its own family rather than another Grainfather. The flow is
   * bottom-up: the pump pushes wort from under the malt pipe up through the bed
   * instead of drawing it down, which is why the mash is thin and even. And the
   * Plus models have a **welded double jacket** for cooling — Speidel's words,
   * "clean and hygienic cooling with water by the welded cooling jacket (no cooling
   * spiral necessary)" — so there is no chiller and no hose to leave wort in, and
   * `chillerLossL` is a real zero rather than a rounded one.
   *
   * Speidel publish the strike volume, the batch, the grain ceiling, the element,
   * the bore and the height, and no boil-off, no efficiency and no malt-pipe
   * volume. The deadspace below is judged from the published bore: a 40 cm bore
   * holds 1.26 L per centimetre of depth, so the few centimetres the pipe stands
   * off the floor come to about 4 L; the 50 cm bore holds 1.96 L/cm and comes to
   * about 8. Both are inside the kettle and cost no water.
   *
   * One published caveat that a brewer will meet: "the use of the cooling jacket at
   * the PLUS is not working properly with the short malt pipe and the half quantity
   * of wort" — half batches lose the jacket, not just some of it.
   */
  {
    ...SHARED,
    id: 'speidel-braumeister-20-plus',
    name: 'Speidel Braumeister 20 L Plus',
    maker: 'Speidel',
    description:
      'The 20 L Braumeister Plus: a 2000 W German malt-pipe urn that pumps the whole 23 L of liquor up through the grain from below for the entire rest, then lifts the pipe out to drain. The Plus adds a welded cooling jacket and a second bottom outlet. Speidel cap it at 6 kg of grain.',
    notes:
      'Speidel publish the 23 L mash-in volume and the 20 L finished batch; the mash is the full liquor, so there is no sparge in the standard process. Boil-off is derived and it is low for the diameter: 2000 W cannot vaporise more than 3.2 L/h at all, and a 40 cm bore spends a third of that holding temperature, which is why the Braumeister boil is famously gentle. Chiller loss is zero because the jacket cools in the vessel. Recalibrate boil-off, then efficiency.',
    nominalBatchVolumeL: 20,
    tags: ['all-in-one', 'electric', 'recirculating', 'malt-pipe'],
    spargeType: 'no-sparge',
    mashTunDeadspaceL: 4.0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 5.0,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 2.0,
    kettleDeadspaceL: 1.0,
    chillerLossL: 0,
    fermenterTrubLossL: 1.5,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 71,
    kettleVolumeL: 25,
    mashTunVolumeL: 25,
  },
  {
    ...SHARED,
    id: 'speidel-braumeister-50-plus',
    name: 'Speidel Braumeister 50 L Plus',
    maker: 'Speidel',
    description:
      'The 50 L Braumeister Plus: same bottom-up malt-pipe mash and welded cooling jacket in a 50 cm bore on a 3200 W element and a 16 A supply, for 50 L of finished beer off a 13 kg grain bill. The size German homebrew clubs and small brewpub pilots settled on.',
    notes:
      "Speidel publish 55 L of mash-in water and a 55 L maximum fill mark; the manual's technical table says 53 L of wort where the shop page says 55, and the row is built on the fill mark. Boil-off is derived: 3200 W caps it at 5.1 L/h and a 50 cm bore is a lot of surface for that element, so a rolling boil sits near 3 L/h — less per liter of element than the narrower B40pro on the same wattage. Chiller loss is zero; the jacket does the work. Half batches with the short malt pipe lose the jacket, per Speidel's own note.",
    nominalBatchVolumeL: 50,
    tags: ['all-in-one', 'electric', 'recirculating', 'malt-pipe', 'double-batch'],
    spargeType: 'no-sparge',
    mashTunDeadspaceL: 8.0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 5.0,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 3.0,
    kettleDeadspaceL: 1.5,
    chillerLossL: 0,
    fermenterTrubLossL: 3.0,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 72,
    kettleVolumeL: 55,
    mashTunVolumeL: 55,
  },
  clawhammer10v120,
  {
    ...clawhammer10v120,
    id: 'clawhammer-10-gal-240v',
    name: 'Clawhammer 10 gal 240 V',
    description:
      'The same 10.5 gallon Clawhammer kettle, basket and pump on a 240 V / 5500 W element and a 30 A panel. Still a 5 gallon batch system — the extra power buys a hard rolling boil and a much shorter heat-up, not a bigger beer.',
    notes:
      'Everything except evaporation is identical to the 120 V sibling, because it is the same vessel. 5500 W lid-off in a squat 14.5 in kettle is roughly double the 120 V rate — about 1.1 gal/h. Recalibrate boil-off first; it is the only number the element actually changes.',
    boilOffRateLPerHour: 4.0,
  },
  {
    ...SHARED,
    id: 'clawhammer-20-gal',
    name: 'Clawhammer 20 gal',
    maker: 'Clawhammer Supply',
    description:
      'The 20 gallon Clawhammer: same basket, pump and plate-chiller layout as the 10 gal, on a 240 V / 5500 W element. Sized for a 10 gallon batch, or a 5 gallon batch with room for a very large grain bill.',
    notes:
      'The kettle is wide and short, so a 5500 W rolling boil evaporates faster than a tall urn of the same volume — start at 1.3 gal/h with the lid off and measure it. Fixed losses are a smaller share of a 10 gallon batch than they are on the 10 gal system.',
    nominalBatchVolumeL: 38,
    tags: ['all-in-one', 'electric', 'recirculating', 'malt-pipe', 'double-batch'],
    spargeType: 'batch',
    mashTunDeadspaceL: 2.0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 5.0,
    kettleDeadspaceL: 1.5,
    chillerLossL: 0.8,
    fermenterTrubLossL: 2.0,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 71,
    kettleVolumeL: 76,
    mashTunVolumeL: 76,
  },
  {
    ...SHARED,
    id: 'spike-solo-10',
    name: 'Spike Solo 10 gal',
    maker: 'Spike Brewing',
    description:
      'A Milwaukee-made 10 gallon all-in-one: 304 kettle, laser-slit grain basket and a side-mounted element. Sold in 120 V and 240 V; the 10 gal is the 5 gallon / 5 gallon everyday system.',
    notes:
      'Spike rate the 10 gal Solo for 2.5–5 gallon batches and sell it as 120 V (15 A / 1800 W) or 240 V (25 A / 6000 W). Boil-off swings hard with that — a 1800 W boil is closer to 0.5 gal/h, a 6000 W boil closer to 1.1. This row sits in the middle at 0.8 gal/h; measure yours. Recirculation is an add-on pump, not built in.',
    nominalBatchVolumeL: 19,
    tags: ['all-in-one', 'electric', 'malt-pipe'],
    spargeType: 'batch',
    mashTunDeadspaceL: 1.2,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 3.0,
    kettleDeadspaceL: 1.0,
    chillerLossL: 0.5,
    fermenterTrubLossL: 1.0,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 71,
    kettleVolumeL: 38,
    mashTunVolumeL: 38,
  },
  {
    ...SHARED,
    id: 'spike-solo-15',
    name: 'Spike Solo 15 gal',
    maker: 'Spike Brewing',
    description:
      'The 15 gallon Solo, 240 V / 5500 W, for a comfortable 6 gallon batch with headroom — or an 8 gallon beer if the grain bill fits. Same basket and tri-clamp layout as the 10 gal, more surface over the element.',
    notes:
      'Spike rates this one for 4–8 gallon batches; 6 gallons is the everyday 6 gallon keg-and-a-bit fill. Recalibrate boil-off first — 5500 W in a 15 gal kettle is a fast evaporator with the lid off.',
    nominalBatchVolumeL: 23,
    tags: ['all-in-one', 'electric', 'malt-pipe'],
    spargeType: 'batch',
    mashTunDeadspaceL: 1.5,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 4.0,
    kettleDeadspaceL: 1.2,
    chillerLossL: 0.5,
    fermenterTrubLossL: 1.2,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 71,
    kettleVolumeL: 57,
    mashTunVolumeL: 57,
  },
  {
    ...SHARED,
    id: 'spike-solo-20',
    name: 'Spike Solo 20 gal',
    maker: 'Spike Brewing',
    description:
      'The 20 gallon Solo, built for a 10 gallon / 10 gallon batch. Same laser-slit basket and 5500 W element as the 15 gal, with the extra volume a double-batch or a high-gravity 5 gallon needs.',
    notes:
      'A 20 gal kettle on 5500 W evaporates faster than the 15 because of surface area, not wattage. Start at 1.3 gal/h lid-off and measure it. Bottom-drain Tank models lose a little less to kettle deadspace than the flat-bottom OG.',
    nominalBatchVolumeL: 38,
    tags: ['all-in-one', 'electric', 'malt-pipe', 'double-batch'],
    spargeType: 'batch',
    mashTunDeadspaceL: 2.0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 5.0,
    kettleDeadspaceL: 1.5,
    chillerLossL: 0.6,
    fermenterTrubLossL: 2.0,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 71,
    kettleVolumeL: 76,
    mashTunVolumeL: 76,
  },
  {
    ...SHARED,
    id: 'spike-trio-herms-10',
    name: 'Spike Trio 10 gal electric HERMS',
    maker: 'Spike Brewing',
    description:
      "Spike's three-vessel Trio: a 15 gallon HLT, mash tun and boil kettle in Spike+ tri-clamp, two 5500 W ULWD elements on 240 V, a HERMS coil in the HLT and two Flow pumps. Spike rate the 15 gal Trio for 5–11 gallon batches; this row is built around a 10 gallon transfer.",
    notes:
      'This is the Spike+ kettle-based system, as opposed to the single-vessel Solo. Spike publish the vessel size, batch range, element count and coil, but no evaporation or deadspace figure — those are class-typical for a 15 gal boil kettle and are the first things to recalibrate. The stepped kettle bottom and side pickup leave a little less behind than a flat floor; drop kettle deadspace if you whirlpool well.',
    nominalBatchVolumeL: 38,
    tags: ['three-vessel', 'electric', 'herms', 'fly-sparge'],
    spargeType: 'fly',
    mashTunDeadspaceL: 0,
    mashTunLossL: 1.5,
    grainAbsorptionLPerKg: 1.0,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 4.0,
    kettleDeadspaceL: 1.5,
    chillerLossL: 0.8,
    fermenterTrubLossL: 2.0,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 72,
    kettleVolumeL: 57,
    mashTunVolumeL: 57,
  },
  /**
   * Bräu Supply, of Reno. The names the catalogue asked for — "Unibräu V3" and
   * "Unibräu Pro" — are both retired: the current single-vessel line is just
   * **Unibräu** (brausupply.com/products/unibrau-10g, /unibrau-20g) and the
   * two-vessel line is **BräuPro** (/products/braupro-homebrewing, /braupro-20g).
   * The V3 name survives in exactly one manufacturer artifact, and it is the useful
   * one: the BeerSmith equipment profile Bräu Supply link from their own product
   * pages is titled `Unibrau V.3-6g batch`, and it is the only place they publish a
   * boil-off or an efficiency at all.
   *
   * From that profile: 10 %/h evaporation, 0.61 gal (2.3 L) over a 60-minute boil,
   * 72 % efficiency, zero mash-tun deadspace, with their own caveat that "brewer
   * may need to adjust these values based on the boil period and the amount of
   * power to the element". Their 2.3 L/h is well under what an unthrottled 5500 W
   * boil does in a 38 L kettle; it describes an EzBoil turned down, and it is used
   * verbatim on the 10G because it is theirs. The 20G has no absolute figure, so
   * their 10 %/h *rate* is applied to that rig's pre-boil instead — about 4.5 L/h.
   *
   * Vessel numbers come from brausupply.com/pages/unibrau-dimensions-and-volumes,
   * which is the closest thing they publish to a spec sheet and which disagrees
   * with their own product pages (it says 2.5–6 gal and 16 lb for the 10G where the
   * product page says "up to ~7 gallons" and a kettle SKU page says 20 lb). The
   * dimensions page wins here; the product-page ceilings read as marketing.
   *
   * BräuPro is not a trim level of the Unibräu, which is why it gets its own rows:
   * it mashes in a bottom-drain DrainPro tun and then **boils in the jacketed
   * conical it will ferment in**. That single fact rewrites the bottom of the
   * volume waterfall — there is no kettle-to-fermenter transfer, so kettle
   * deadspace and chiller loss are both genuinely zero and the cone is the only
   * post-boil loss left.
   */
  {
    ...SHARED,
    id: 'brau-supply-unibrau-10',
    name: 'Bräu Supply Unibräu 10G',
    maker: 'Bräu Supply',
    description:
      'A 38 L single-vessel electric system on a 5500 W ultra-low-watt-density element and a 30 A dryer circuit, with a solid perforated stainless basket rather than a mesh bag and an integrated pump. Bräu Supply size it for 2.5 to 6 gallon batches on up to 16 lb of grain.',
    notes:
      "Boil-off and brewhouse efficiency are Bräu Supply's own published figures, from the BeerSmith profile on their product page: 2.3 L/h and 72 %. The boil-off is low for 5500 W because it describes a throttled EzBoil — if you boil at full power expect to nearly double it, which is their own advice. Mash thickness is derived from the same profile's 5.23 gal mash volume. Their profile files zero mash-tun deadspace; the 1.5 L here is the gap under the basket, which is inside the kettle either way and costs no water.",
    nominalBatchVolumeL: 23,
    tags: ['all-in-one', 'electric', 'recirculating', 'malt-pipe'],
    spargeType: 'batch',
    mashTunDeadspaceL: 1.5,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 3.5,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 2.3,
    kettleDeadspaceL: 1.0,
    chillerLossL: 0.4,
    fermenterTrubLossL: 1.0,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 72,
    kettleVolumeL: 38,
    mashTunVolumeL: 38,
  },
  {
    ...SHARED,
    id: 'brau-supply-unibrau-20',
    name: 'Bräu Supply Unibräu 20G',
    maker: 'Bräu Supply',
    description:
      'The 76 L Unibräu: a 69 L laser-cut grain basket, 1.5 mm walls and up to 40 lb of grain, on the same 5500 W element as the 10 gallon. Bräu Supply rate it for 5 to 12 gallon batches — a double batch, or a very large beer at single-batch size.',
    notes:
      "The element does not scale with the kettle, so the boil rate barely does either: Bräu Supply publish an evaporation *rate* of 10 %/h rather than a volume, and applied to this rig's pre-boil that is about 4.5 L/h. Brewhouse efficiency is their published 72 %. Mash thickness drops against the 10G only because the same basket takes a much bigger bill. Recalibrate boil-off first — it is the number their own note tells you depends on how hard you drive the element.",
    nominalBatchVolumeL: 38,
    tags: ['all-in-one', 'electric', 'recirculating', 'malt-pipe', 'double-batch'],
    spargeType: 'batch',
    mashTunDeadspaceL: 2.5,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 3.0,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 4.5,
    kettleDeadspaceL: 1.5,
    chillerLossL: 0.6,
    fermenterTrubLossL: 2.0,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 72,
    kettleVolumeL: 76,
    mashTunVolumeL: 76,
  },
  {
    ...SHARED,
    id: 'brau-supply-braupro-10',
    name: 'Bräu Supply BräuPro 10G',
    maker: 'Bräu Supply',
    description:
      'Two vessels: a 40 L bottom-drain DrainPro mash tun with a false bottom and a vorlauf arm, and a 50 L jacketed conical unitank that is both the boil kettle and the fermenter. A 5500 W element goes in the conical. Bräu Supply publish a 19 to 38 L brew range.',
    notes:
      'Boiling in the fermenter is what makes this row different from every other one here: the wort is never transferred hot, so kettle deadspace and chiller loss are both zero and the cone is the only loss after the boil. The mash tun is a separate vessel that drains into the conical and never gets it back, so its 2 L is a loss, not deadspace. It is single-wall stainless with no heat in it, so a resting mash cools like a cooler — 2 °C/h, which decoction reads. Boil-off is derived: 5500 W in a 40 cm conical with one open port is a much quieter boil than the same element in an open kettle.',
    nominalBatchVolumeL: 30,
    tags: ['two-vessel', 'electric', 'batch-sparge'],
    spargeType: 'batch',
    mashTunDeadspaceL: 0,
    mashTunLossL: 2.0,
    grainAbsorptionLPerKg: 1.0,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 2,
    boilOffRateLPerHour: 3.5,
    kettleDeadspaceL: 0,
    chillerLossL: 0,
    fermenterTrubLossL: 2.5,
    mashEfficiencyPercent: 75,
    brewhouseEfficiencyPercent: 70,
    kettleVolumeL: 50,
    mashTunVolumeL: 40,
  },
  {
    ...SHARED,
    id: 'brau-supply-braupro-20',
    name: 'Bräu Supply BräuPro 20G',
    maker: 'Bräu Supply',
    description:
      'The 20 gallon BräuPro: an 80 L DrainPro mash tun into a 100 L jacketed conical that boils and then ferments the beer. Same 5500 W element as the 10G — Bräu Supply do not scale it — and a published brew range of 38 to 76 L.',
    notes:
      'Same architecture and the same zeroes as the 10G: no hot transfer, so no kettle deadspace and no chiller loss. The nominal batch is set at 57 L rather than the published 76 L ceiling because a 100 L unitank fermenting 76 L has nowhere to put a krausen. Boil-off moves barely at all from the 10G, because the element is identical and evaporation is power-limited long before it is surface-limited. Recalibrate the mash-tun loss for your false bottom and dip tube.',
    nominalBatchVolumeL: 57,
    tags: ['two-vessel', 'electric', 'batch-sparge', 'double-batch'],
    spargeType: 'batch',
    mashTunDeadspaceL: 0,
    mashTunLossL: 3.0,
    grainAbsorptionLPerKg: 1.0,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 2,
    boilOffRateLPerHour: 3.8,
    kettleDeadspaceL: 0,
    chillerLossL: 0,
    fermenterTrubLossL: 4.0,
    mashEfficiencyPercent: 75,
    brewhouseEfficiencyPercent: 70,
    kettleVolumeL: 100,
    mashTunVolumeL: 80,
  },
  brewHardwareEbiab10,
  withSteamSlayer(brewHardwareEbiab10, {
    id: 'brew-hardware-ebiab-10-steam-slayer',
    name: 'Brew Hardware eBIAB 10 gal + Steam Slayer',
    description:
      "The 10 gal eBIAB with Brew Hardware's lid-on Steam Slayer condenser. Same 10 gallon kettle, bag and manifold; the boil runs at about half power so steam never leaves the room.",
    notes:
      "Brew Hardware's own guidance is half the lid-off evaporation — 1.1 gal/h open becomes 0.5 gal/h here, in line with their shop kettle (1.5 → 0.75 gal/h). Recalibrate if you run more than ~30 % power or a harder spray. Use the lid-off 10 gal preset if you boil uncovered.",
    boilOffRateLPerHour: 2.0,
  }),
  brewHardwareEbiab15,
  withSteamSlayer(brewHardwareEbiab15, {
    id: 'brew-hardware-ebiab-15-steam-slayer',
    name: 'Brew Hardware eBIAB 15 gal + Steam Slayer',
    description:
      'The 15 gal eBIAB with the Steam Slayer: lid on, spray condenser in the upper TC port, 5500 W turned down to about half. Same 5 gallon batch and 15 gallon Spike kettle as the open-boil sibling.',
    notes:
      'Half of the 1.2 gal/h lid-off rate is 0.6 gal/h, which matches what owners report on a 15 gal / 5500 W kettle at ~28–32 % power (~0.6–0.75 gal/h). Measure yours — spray nozzle and tap pressure move it. Hop stands still work; you lift the lid.',
    boilOffRateLPerHour: 2.25,
  }),
  brewHardwareEbiab20,
  withSteamSlayer(brewHardwareEbiab20, {
    id: 'brew-hardware-ebiab-20-steam-slayer',
    name: 'Brew Hardware eBIAB 20 gal + Steam Slayer',
    description:
      'The 20 gal eBIAB with the Steam Slayer on the wider 20 gallon kettle. Same 10 gallon batch and recirculating bag mash; evaporation drops to about half because the lid stays on and the element is turned down.',
    notes:
      'A wide 20 gal still evaporates a bit more than the 15 even with the condenser — 0.7 gal/h is half the lid-off 1.5 gal/h. Recalibrate after the first batch; the wide-body Steam Slayer is the one Brew Hardware sells for this diameter.',
    boilOffRateLPerHour: 2.8,
  }),
  brewHardwareHerms15,
  withSteamSlayer(brewHardwareHerms15, {
    id: 'brew-hardware-herms-15-steam-slayer',
    name: 'Brew Hardware 15 gal HERMS + Steam Slayer',
    description:
      'The typical 15 gal Brew Hardware HERMS with a Steam Slayer on the boil kettle. Mash tun, HLT and fly sparge are unchanged; only the kettle boil is lid-on and condensed.',
    notes:
      'Same half-rate rule as the eBIAB variants: 1.1 gal/h lid-off becomes 0.5 gal/h. The condenser lives on the boil kettle only — HERMS coil performance in the HLT does not change. Recalibrate if your kettle is a 20 gal instead of a 15.',
    boilOffRateLPerHour: 2.0,
  }),
  {
    ...SHARED,
    id: 'ss-brewtech-ebrewing-10',
    name: 'Ss Brewtech eBrewing 10 gal',
    maker: 'Ss Brewtech',
    description:
      "Ss Brewtech's 10 gallon eKettle 2.0 with the Brew Basket: 5500 W halo element, welded tri-clamp ports and a full-volume mash in one vessel. The 5 gallon / 1V kit, and the usual first electric system for brewers already in their conical lineup.",
    notes:
      'Full-volume basket mash, so efficiency is a few points below a sparged Infussion. The 10 gal kettle is tight on a 5 gallon batch — watch pre-boil volume on big beers. Recalibrate boil-off; 5500 W lid-off in a tall 10 gal is closer to 0.9 gal/h than a wide 20 gal.',
    nominalBatchVolumeL: 19,
    tags: ['all-in-one', 'electric', 'biab', 'full-volume'],
    spargeType: 'biab-full-volume',
    mashTunDeadspaceL: 0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 5.5,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 3.5,
    kettleDeadspaceL: 1.2,
    chillerLossL: 0.5,
    fermenterTrubLossL: 1.0,
    mashEfficiencyPercent: 74,
    brewhouseEfficiencyPercent: 68,
    kettleVolumeL: 38,
    mashTunVolumeL: 38,
  },
  /**
   * The SVBS is the most completely published rig in this file, and the numbers
   * are not on the product page — they are in Ss Brewtech's own support articles.
   * ssbrewtech.com/products/svbs gives the 12 gallon vessel, the 4600 W halo
   * element, the 240 V 30 A supply, 5–6 gallon batches and a 20 lb mash ceiling.
   * Their KB article "Configuring Brewing Software for the SVBS" then publishes an
   * entire equipment profile: **1 gal/h boil-off**, 11 gal mash volume, **0 gal**
   * mash-tun dead space, a **recoverable** deadspace of 2 gal at a 12 lb bill (4
   * gal at 18 lb), 0.25 gal lost to trub and chiller together, and **69 % mash
   * efficiency**. Five of the numbers below are theirs.
   *
   * Two judgements sit on top. Their 0.25 gal is a single figure for trub *and*
   * chiller, so it is split 0.7 L in the kettle and 0.25 L in the hoses; the sum
   * is what they published. And the sparge type is `no-sparge` rather than
   * `biab-full-volume`, which is the difference between this row and the eBrewing
   * one above it: the eBrewing kit mashes in a bag that gives water back when it
   * is squeezed, while the SVBS mashes in a rigid photo-etched basket that does
   * not — and that basket has a published under-volume, which a bag never does.
   */
  {
    ...SHARED,
    id: 'ss-brewtech-svbs-12',
    name: 'Ss Brewtech SVBS 12 gal',
    maker: 'Ss Brewtech',
    description:
      "Ss Brewtech's purpose-built single vessel: a 12 gallon kettle with an etched mash basket, a center recirculation pipe, a bottom whirlpool inlet, an integrated pump on a pinch valve and one touchscreen controller. A 4600 W element on 240 V, 5 to 6 gallon batches, 20 lb of grain.",
    notes:
      "Not the eKettle-and-Brew-Basket kit: that one is modular, 5500 W and assembled from parts, where this is one machine with the pump and the flow control built in. Boil-off (1 gal/h), the 2 gal recoverable basket deadspace and the 69 % mash efficiency are all Ss Brewtech's published figures and are used verbatim; 69 % is honest for a full-volume mash with no rinse. Deadspace goes to 4 gal on an 18 lb bill, per the same article. The one thing to recalibrate is boil-off if you run the lid cracked.",
    nominalBatchVolumeL: 19,
    tags: ['all-in-one', 'electric', 'recirculating', 'full-volume'],
    spargeType: 'no-sparge',
    mashTunDeadspaceL: 7.6,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 7.7,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 3.8,
    kettleDeadspaceL: 0.7,
    chillerLossL: 0.25,
    fermenterTrubLossL: 1.0,
    mashEfficiencyPercent: 69,
    brewhouseEfficiencyPercent: 63,
    kettleVolumeL: 45.4,
    mashTunVolumeL: 45.4,
  },
  {
    ...SHARED,
    id: 'ss-brewtech-infussion-10',
    name: 'Ss Brewtech InfuSsion 10 gal',
    maker: 'Ss Brewtech',
    description:
      'The 10 gallon foam-insulated InfuSsion mash tun paired with a 10 gal eKettle: sloped floor, gasketed false bottom and a recirculation bulkhead. The common Ss three-vessel 5 gallon electric upgrade from a cooler.',
    notes:
      'Ss advertise near-zero mash-tun deadspace; 0.1 gal is the honest leftover in the valve and hose, not a claim the floor is dry. The tun is a separate vessel, so that 0.1 gal never reaches the kettle and is recorded as a loss. A 10 gal kettle is tight for a 5 gallon all-grain boil — drop the batch a quart or move the kettle to 20 gal if pre-boil keeps kissing the rim.',
    nominalBatchVolumeL: 19,
    tags: ['three-vessel', 'electric', 'recirculating'],
    spargeType: 'batch',
    mashTunDeadspaceL: 0,
    mashTunLossL: 0.4,
    grainAbsorptionLPerKg: 1.0,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 1.5,
    boilOffRateLPerHour: 3.5,
    kettleDeadspaceL: 1.2,
    chillerLossL: 0.6,
    fermenterTrubLossL: 1.0,
    mashEfficiencyPercent: 75,
    brewhouseEfficiencyPercent: 69,
    kettleVolumeL: 38,
    mashTunVolumeL: 38,
  },
  {
    ...SHARED,
    id: 'blichmann-herms-10',
    name: 'Blichmann 10 gal electric HERMS',
    maker: 'Blichmann Engineering',
    description:
      "Blichmann's 10 gallon electric HERMS: two 15 gallon BoilerMaker vessels and a 20 gallon electric boil kettle, BoilCoil elements, RipTide pumps and BrewCommander control. The turnkey US three-vessel that a lot of 10 gallon breweries start from.",
    notes:
      'The 20 gal boil kettle gives real headspace on a 10 gallon batch, which is why boil-off is closer to 1.2 gal/h than a 15 gal kettle at the same wattage. Recalibrate the Therminator and hose loss if you skip the plate chiller or run a longer transfer.',
    nominalBatchVolumeL: 38,
    tags: ['three-vessel', 'electric', 'herms', 'fly-sparge'],
    spargeType: 'fly',
    mashTunDeadspaceL: 0,
    mashTunLossL: 1.5,
    grainAbsorptionLPerKg: 1.0,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 4.5,
    kettleDeadspaceL: 2.0,
    chillerLossL: 1.0,
    fermenterTrubLossL: 2.0,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 72,
    kettleVolumeL: 76,
    mashTunVolumeL: 57,
  },
  /**
   * The BrewEasy is two BoilerMaker kettles stacked, and the whole system is one
   * idea: the mash tun sits directly above the boil kettle, the pump lifts wort up
   * into it and gravity returns it, continuously, for the whole rest. The lower
   * kettle's element is therefore also the mash's heater — a kettle RIMS in two
   * pots — which is why `mashStandingLossCPerHour` is zero on a rig with no
   * insulation anywhere on it.
   *
   * Blichmann's manual (blichmannengineering.com, BrewEasy ALK G2 Manual V6)
   * publishes the kettle pairings verbatim, and they are the only vessel numbers
   * that matter here: 5 gal batch = 7.5 gal mash tun over a 10 gal boil kettle;
   * 10 gal batch = 15 over 20; 20 gal batch = 30 over 30. Four heat options are
   * sold — 120 V, 240 V, a 240 V surface element and gas — and these rows take the
   * **240 V BoilCoil**, because that is the option whose wattage Blichmann publish
   * per kettle size (3750 W in the 10 gal, 5000 W in the 20, 5750 W in the 30) and
   * so the only one a boil-off can be derived from. The gas configuration ships a
   * HellFire rated at 140,000 BTU/h and would evaporate faster; the electric
   * numbers are the ones this file can defend.
   *
   * The deadspace-versus-loss call is the interesting one, and it goes the other
   * way from every other multi-vessel rig here. The file's rule is that a separate
   * tun's leftovers are a *loss* because they never reach the kettle — but the
   * BrewEasy's tun is bolted directly over its kettle and drains into it by
   * gravity. The volume under its false bottom is recovered exactly the way a malt
   * pipe's is. So it is filed as deadspace, and it costs no water.
   */
  {
    ...SHARED,
    id: 'blichmann-breweasy-5',
    name: 'Blichmann BrewEasy 5 gal',
    maker: 'Blichmann Engineering',
    description:
      'The 5 gallon BrewEasy: a 7.5 gallon BoilerMaker mash tun stacked over a 10 gallon boil kettle, with a 3750 W 240 V BoilCoil in the lower pot, a RipTide pump and BrewCommander control. Wort recirculates between the two for the whole mash, so the kettle element holds the rest.',
    notes:
      'The whole liquor is in the system from the start and there is no sparge, which is where the efficiency sits relative to a fly-sparged three-vessel. The mash tun drains into the kettle beneath it, so its under-false-bottom volume is deadspace rather than a loss and costs you no strike water — the opposite of how a HERMS tun is filed. Boil-off is derived from the published 3750 W: a 5.9 L/h ceiling, and about half of it at a rolling boil in a 35 cm kettle. Blichmann publish 18 lb of grain as the ceiling for this pairing.',
    nominalBatchVolumeL: 19,
    tags: ['two-vessel', 'electric', 'recirculating', 'full-volume'],
    spargeType: 'no-sparge',
    mashTunDeadspaceL: 2.0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 1.0,
    mashThicknessLPerKg: 5.0,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 2.8,
    kettleDeadspaceL: 1.2,
    chillerLossL: 0.5,
    fermenterTrubLossL: 1.0,
    mashEfficiencyPercent: 76,
    brewhouseEfficiencyPercent: 70,
    kettleVolumeL: 38,
    mashTunVolumeL: 28.4,
  },
  {
    ...SHARED,
    id: 'blichmann-breweasy-10',
    name: 'Blichmann BrewEasy 10 gal',
    maker: 'Blichmann Engineering',
    description:
      'The 10 gallon pairing: a 15 gallon mash tun over a 20 gallon boil kettle on a 5000 W 240 V BoilCoil. Same continuous recirculation and same BrewCommander as the 5 gallon — the kettles change and nothing else does.',
    notes:
      'Blichmann do not publish a grain ceiling for this pairing, only for the 10 gallon kettle at 18 lb, so size big beers against the tun rather than a spec. Boil-off is derived from the published 5000 W and the 45 cm kettle bore. Fixed losses are a smaller share of a 10 gallon batch than a 5, which is the whole argument for stepping up. The mash tun still drains into the kettle, so its volume stays deadspace.',
    nominalBatchVolumeL: 38,
    tags: ['two-vessel', 'electric', 'recirculating', 'full-volume', 'double-batch'],
    spargeType: 'no-sparge',
    mashTunDeadspaceL: 3.0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 1.0,
    mashThicknessLPerKg: 4.5,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 4.5,
    kettleDeadspaceL: 1.8,
    chillerLossL: 0.8,
    fermenterTrubLossL: 2.0,
    mashEfficiencyPercent: 76,
    brewhouseEfficiencyPercent: 70,
    kettleVolumeL: 76,
    mashTunVolumeL: 57,
  },
  {
    ...SHARED,
    id: 'blichmann-breweasy-20',
    name: 'Blichmann BrewEasy 20 gal',
    maker: 'Blichmann Engineering',
    description:
      'The largest BrewEasy: two 30 gallon BoilerMakers, one over the other, on a 5750 W 240 V BoilCoil. Twenty gallons of finished beer out of a system that still fits under a garage ceiling and still runs off one 30 A circuit.',
    notes:
      'The element only grows 15 % from the 10 gallon pairing while the kettle bore grows by a quarter, so evaporation barely moves — 5 L/h against 4.5. That is the ceiling talking, not the kettle. Everything else scales the way you would expect: fixed losses are the smallest share of the batch anywhere in the BrewEasy family, which is the extra point of brewhouse efficiency. Blichmann quote a mid-February ship estimate on the 30 gallon kettles; check availability before planning around it.',
    nominalBatchVolumeL: 76,
    tags: ['two-vessel', 'electric', 'recirculating', 'full-volume', 'double-batch'],
    spargeType: 'no-sparge',
    mashTunDeadspaceL: 4.5,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 1.0,
    mashThicknessLPerKg: 4.5,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 5.0,
    kettleDeadspaceL: 2.5,
    chillerLossL: 1.2,
    fermenterTrubLossL: 4.0,
    mashEfficiencyPercent: 76,
    brewhouseEfficiencyPercent: 71,
    kettleVolumeL: 114,
    mashTunVolumeL: 114,
  },
  /**
   * MoreBeer's gas BrewSculpture — the three-tier propane HERMS that a generation
   * of US 10 and 20 gallon brewers learned on. It is **discontinued on MoreBeer's
   * own site**: morebeer.com now serves the all-electric BrewBuilt Low Rider V5
   * from the old Tippy Dump URL, and site search for "brewsculpture" returns
   * nothing. The vessel numbers below are MoreBeer's own copy, from the archived
   * product page (web.archive.org capture of
   * morebeer.com/products/tippy-dump-digital-brewsculpture-v4.html, 2025-11-01),
   * and authorised dealers still list the same SKUs.
   *
   * Two naming corrections, because they change what the rows are. MoreBeer never
   * sold "Digital" and "Classic" — the tiers were Standard, Digital and Digital
   * Deluxe. And the sizes were never "15 gal" and "20 gal": they were **Large
   * (10–15 gal batches, three 22 gallon kettles)** and **XL (15–25 gal, three 30
   * gallon kettles)**, and "20 gallon" was legacy shorthand for the XL's 2050 SKU
   * family.
   *
   * There is one row per size and not one per tier, and unlike the Clawhammer
   * 120/240 V split that precedent does not apply here. Clawhammer's two SKUs
   * differ by an *element*, which moves evaporation and nothing else — a number
   * this file carries. Digital adds a control panel, thermowells, float switches, a
   * convoluted rather than plain HERMS coil and an automated HLT burner. Every one
   * of those changes how the brew day feels and not one of them changes a volume, a
   * loss or a rate. A second row would be two identical rows with different names.
   *
   * MoreBeer publish no BTU figure for the "21 Jet Burners" at any tier, and no
   * deadspace, boil-off or efficiency. The boil rates are scaled off
   * `generic-cooler-10-gal`'s propane row by kettle bore area, per the header.
   */
  {
    ...SHARED,
    id: 'morebeer-brewsculpture-large',
    name: 'MoreBeer BrewSculpture Large',
    maker: 'MoreBeer',
    description:
      'The Large three-tier gas sculpture: three 22 gallon kettles on a stainless frame, three jet burners, two March pumps and a convoluted copper HERMS coil in the hot liquor tank that doubles as the wort chiller. MoreBeer rated it for 10 to 15 gallon batches.',
    notes:
      "Discontinued on MoreBeer's site — the URL now serves the electric V5 — but still in garages and still on dealer shelves. The HERMS coil holds the mash at setpoint, so a decoction pull needs no standing-loss correction. Boil-off is derived, not published: an uncovered propane boil under a 22 gallon kettle has about a third more surface than the 15 gallon propane row in this table, and no maker BTU figure exists to do better. Recalibrate it first, then the mash-tun loss for your false bottom.",
    nominalBatchVolumeL: 45,
    tags: ['three-vessel', 'herms', 'propane', 'fly-sparge'],
    spargeType: 'fly',
    mashTunDeadspaceL: 0,
    mashTunLossL: 2.0,
    grainAbsorptionLPerKg: 1.0,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 5.5,
    kettleDeadspaceL: 2.0,
    chillerLossL: 1.5,
    fermenterTrubLossL: 2.5,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 72,
    kettleVolumeL: 83,
    mashTunVolumeL: 83,
  },
  {
    ...SHARED,
    id: 'morebeer-brewsculpture-xl',
    name: 'MoreBeer BrewSculpture XL',
    maker: 'MoreBeer',
    description:
      'The XL: the same three-tier gas HERMS frame carrying three 30 gallon kettles instead of three 22s, rated by MoreBeer for 15 to 25 gallon batches. Two March pumps, an auto-sparge float switch in the mash tun, and the HERMS coil doing double duty as the chiller.',
    notes:
      'Also discontinued at MoreBeer. A 30 gallon kettle over an open flame is the fastest evaporator in this table after the 1 bbl rigs — the figure here is the 15 gallon propane row scaled by bore area, since MoreBeer publish no BTU rating for the burners at any tier. At a 20 gallon batch the fixed losses barely register, which is why brewhouse efficiency sits at the top of the three-vessel band. Measure boil-off on your first batch; wind and burner height move it more than the kettle does.',
    nominalBatchVolumeL: 76,
    tags: ['three-vessel', 'herms', 'propane', 'fly-sparge', 'double-batch'],
    spargeType: 'fly',
    mashTunDeadspaceL: 0,
    mashTunLossL: 3.0,
    grainAbsorptionLPerKg: 1.0,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 0,
    boilOffRateLPerHour: 6.5,
    kettleDeadspaceL: 3.0,
    chillerLossL: 2.0,
    fermenterTrubLossL: 4.0,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 73,
    kettleVolumeL: 114,
    mashTunVolumeL: 114,
  },
  /**
   * Ruby Street Brewing, of Fort Collins, still trading and still publishing live
   * pages and prices. Their gas rigs are the direct-fire counterpoint to the
   * HERMS sculptures above: rubystreetbrewing.com/brewing-systems/fusion-15-gas-fired
   * and /brewing-systems/alpha-ruby-1-bbl-gas both state plainly that the base gas
   * configuration has no heat exchanger — every vessel sits on its own cast iron
   * burner and the pumps recirculate. (The electric Fusion 15 does get a HERMS coil
   * and two 5500 W elements; it is a different rig and not modelled here.)
   *
   * Ruby Street publish vessel count and volume, batch size, pump count, burner
   * *diameter* and frame dimensions — and no BTU rating for any burner, no
   * deadspace, no boil-off and no efficiency. Boil rates are scaled from
   * `generic-cooler-10-gal`'s propane row by kettle bore, as with the MoreBeer rows.
   *
   * The standing-loss figures are worth reading against each other, because they
   * are the same vessel at two sizes. The Fusion's 15.5 gallon mash tun is a bare
   * single-wall kettle with the burner off under it, which is the schema's 4 °C/h
   * band word for word. The Alpha Ruby's tun is just as bare, but the schema's own
   * argument for the 1 °C/h band is surface area per litre rather than insulation,
   * and a 50 gallon vessel has roughly half the surface per litre of a 15: 4 ÷
   * ∛(190/59) ≈ 2.7, so it takes the 2 °C/h band and stops there because nothing
   * about it is insulated.
   */
  {
    ...SHARED,
    id: 'ruby-street-fusion-15-gas',
    name: 'Ruby Street Fusion 15 gas',
    maker: 'Ruby Street Brewing',
    description:
      'A single-tier gas rig on casters: three 15.5 gallon kettles, three 6 inch cast iron burners on a manifold, two pumps with remote switches and two false bottoms. Direct fire, no heat exchanger — Ruby Street build it for 10 gallon batches and sell the HERMS coil on the electric version.',
    notes:
      "Direct-firing the mash tun under recirculation is why mash efficiency sits just below the HERMS three-vessel rows: the bed is rinsed properly but the heat comes in at one hot spot rather than through a coil. Nothing about the tun is insulated, so a resting mash with the burner off cools like a bare kettle, which decoction reads as the 4 °C/h band — relight the burner and the correction goes away. Boil-off is scaled from this table's propane row; Ruby Street publish burner diameter but no BTU.",
    nominalBatchVolumeL: 38,
    tags: ['three-vessel', 'propane', 'fly-sparge'],
    spargeType: 'fly',
    mashTunDeadspaceL: 0,
    mashTunLossL: 1.5,
    grainAbsorptionLPerKg: 1.0,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 4,
    boilOffRateLPerHour: 4.0,
    kettleDeadspaceL: 1.5,
    chillerLossL: 0.8,
    fermenterTrubLossL: 2.0,
    mashEfficiencyPercent: 76,
    brewhouseEfficiencyPercent: 70,
    kettleVolumeL: 59,
    mashTunVolumeL: 59,
  },
  {
    ...SHARED,
    id: 'ruby-street-alpha-ruby-1-bbl-gas',
    name: 'Ruby Street Alpha Ruby 1 bbl gas',
    maker: 'Ruby Street Brewing',
    description:
      'Three 50-plus gallon tanks with internal volume markings, three 10 inch cast iron burners and two pumps on 1.5 inch tri-clover, on a frame nearly seven feet wide. Ruby Street size it for a true barrel — 31 gallons — and direct-fire every vessel.',
    notes:
      "This is the branded, direct-fire alternative to the generic 1 bbl row: that one models a jacketed commercial house and converts a few points better, this one is three open kettles over flame and does not. Boil-off is the highest in the table because a 50 gallon uncovered kettle over a 10 inch burner has more open surface than anything else here; it is scaled by bore from the propane row, since no BTU figure is published. Standing loss is 2 °C/h — the tun is bare, but a 190 L vessel has half the surface per liter of the Fusion's.",
    nominalBatchVolumeL: 117,
    tags: ['three-vessel', 'propane', 'fly-sparge', 'nano', 'commercial'],
    spargeType: 'fly',
    mashTunDeadspaceL: 0,
    mashTunLossL: 5.0,
    grainAbsorptionLPerKg: 1.0,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 2,
    boilOffRateLPerHour: 9.0,
    kettleDeadspaceL: 6.0,
    chillerLossL: 2.5,
    fermenterTrubLossL: 8.0,
    mashEfficiencyPercent: 78,
    brewhouseEfficiencyPercent: 72,
    kettleVolumeL: 190,
    mashTunVolumeL: 190,
  },
  {
    ...SHARED,
    id: 'generic-cooler-10-gal',
    name: 'Generic 10 gal cooler + kettle',
    maker: 'Generic',
    description:
      'The classic three-vessel homebrew rig: a 10 gallon insulated drinks cooler with a manifold as the mash tun, a propane burner under a 15 gallon kettle, and batch sparging in between.',
    notes:
      'A static mash bed converts a few points worse than a recirculated one, and an uncovered propane boil is the fastest evaporator in this table. Both are worth measuring rather than trusting.',
    nominalBatchVolumeL: 23,
    tags: ['cooler', 'three-vessel', 'batch-sparge', 'propane'],
    spargeType: 'batch',
    mashTunDeadspaceL: 0,
    mashTunLossL: 1.0,
    grainAbsorptionLPerKg: 1.0,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 2,
    boilOffRateLPerHour: 4.0,
    kettleDeadspaceL: 1.5,
    chillerLossL: 0.5,
    fermenterTrubLossL: 1.5,
    mashEfficiencyPercent: 72,
    brewhouseEfficiencyPercent: 65,
    kettleVolumeL: 57,
    mashTunVolumeL: 38,
  },
  /**
   * The two small-batch rows, and the only ones in this file with no maker and no
   * spec sheet anywhere behind them — they are a kitchen pot on a kitchen stove.
   * Every number is judged, and the two that matter are judged from the stove
   * rather than the pot.
   *
   * Evaporation is the first. A domestic burner or a hob element delivers on the
   * order of 1.5–2 kW to the bottom of a small pot and loses a good deal of it
   * around the sides, so the wattage ceiling that governs the electric rows above
   * is not the binding constraint here — the pot's narrow open surface is. These
   * rows use 1.1 and 1.5 L/h, which is the 0.3–0.4 gal/h a stovetop brewer
   * measures, and the wider pot is the faster one.
   *
   * Fixed losses are the second, and they are the reason these rows exist at all.
   * Half a litre left under the pickup is nothing on a 19 L batch and a seventh of
   * a 3.8 L one. Mash efficiency is the same 72 % as any full-volume BIAB, but
   * brewhouse efficiency falls to 60 % on the 1 gallon row purely because the same
   * absolute losses are being taken out of a much smaller beer. A brewer scaling a
   * 5 gallon recipe down without that correction will miss their gravity every
   * time, which is exactly what these presets are for.
   */
  {
    ...SHARED,
    id: 'generic-stovetop-biab-1-gal',
    name: 'Generic 1 gal stovetop BIAB',
    maker: 'Generic',
    description:
      'A 3 gallon kitchen stockpot, a bag and one gallon of finished beer — the smallest all-grain brew day there is, and the one most people use to test a recipe before committing twenty pounds of grain to it. No pump, no chiller, an ice bath in the sink.',
    notes:
      'Judged throughout; no maker publishes any of this because there is no maker. Boil-off is 0.3 gal/h, which is what a small pot on a domestic hob actually does. The number to watch is not efficiency but the fixed losses: 0.4 L on the trub and 0.2 L under the pickup are a sixth of the batch, which is why brewhouse efficiency reads 60 % against the same 72 % mash as a full-size BIAB. Scale a 5 gallon recipe down against brewhouse, not mash, or you will land light.',
    nominalBatchVolumeL: 3.8,
    tags: ['biab', 'single-vessel', 'full-volume', 'small-batch'],
    spargeType: 'biab-full-volume',
    mashTunDeadspaceL: 0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 6.5,
    mashStandingLossCPerHour: 4,
    boilOffRateLPerHour: 1.1,
    kettleDeadspaceL: 0.2,
    chillerLossL: 0,
    fermenterTrubLossL: 0.4,
    mashEfficiencyPercent: 72,
    brewhouseEfficiencyPercent: 60,
    kettleVolumeL: 11.4,
    mashTunVolumeL: 11.4,
  },
  {
    ...SHARED,
    id: 'generic-stovetop-biab-2-5-gal',
    name: 'Generic 2.5 gal stovetop BIAB',
    maker: 'Generic',
    description:
      'A 5 gallon pot on the stove for a half batch — two and a half gallons, a case of bottles, and a grain bill small enough to mash in the whole liquor without a second vessel. The size most stovetop brewers settle on once the 1 gallon batches stop lasting long enough.',
    notes:
      'Judged, like its smaller sibling, and boil-off is the 0.4 gal/h a 5 gallon pot loses — a little more than the 3 gallon because the surface is wider, not because the hob is hotter. The fixed losses are still a real share of the batch but half the bite they take out of a 1 gallon brew, which is the whole difference in brewhouse efficiency between the two rows. Watch the boil-over margin: a full-volume mash in a 19 L pot leaves very little headspace.',
    nominalBatchVolumeL: 9.5,
    tags: ['biab', 'single-vessel', 'full-volume', 'small-batch'],
    spargeType: 'biab-full-volume',
    mashTunDeadspaceL: 0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 5.5,
    mashStandingLossCPerHour: 4,
    boilOffRateLPerHour: 1.5,
    kettleDeadspaceL: 0.4,
    chillerLossL: 0,
    fermenterTrubLossL: 0.8,
    mashEfficiencyPercent: 72,
    brewhouseEfficiencyPercent: 63,
    kettleVolumeL: 19,
    mashTunVolumeL: 19,
  },
  {
    ...SHARED,
    id: 'generic-biab-20l',
    name: 'Generic BIAB 5 gal',
    maker: 'Generic',
    description:
      'One kettle, one bag, the whole liquor in from the start. The cheapest way into all-grain brewing and still the fastest brew day, at the cost of a few points of efficiency.',
    notes:
      'Full-volume mashing means no sparge rinse, which is where the efficiency goes. Grain absorption is 0.8 L/kg because a squeezed bag gives water back that a lauter tun keeps.',
    nominalBatchVolumeL: 15,
    tags: ['biab', 'single-vessel', 'full-volume'],
    spargeType: 'biab-full-volume',
    mashTunDeadspaceL: 0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 6.0,
    mashStandingLossCPerHour: 4,
    boilOffRateLPerHour: 3.0,
    kettleDeadspaceL: 1.0,
    chillerLossL: 0,
    fermenterTrubLossL: 1.0,
    mashEfficiencyPercent: 72,
    brewhouseEfficiencyPercent: 66,
    kettleVolumeL: 20,
    mashTunVolumeL: 20,
  },
  {
    ...SHARED,
    id: 'generic-biab-35l',
    name: 'Generic BIAB 9 gal',
    maker: 'Generic',
    description:
      'The same one-kettle, one-bag method in a 9 gallon pot, sized for a full 6 gallon batch with the whole liquor in the vessel at once.',
    notes:
      'Thickness is lower than the 5 gallon version only because the same full volume is spread over a larger grain bill; it is still a full-volume mash. A wider pot evaporates a little faster.',
    nominalBatchVolumeL: 23,
    tags: ['biab', 'single-vessel', 'full-volume'],
    spargeType: 'biab-full-volume',
    mashTunDeadspaceL: 0,
    mashTunLossL: 0,
    grainAbsorptionLPerKg: 0.8,
    mashThicknessLPerKg: 5.0,
    mashStandingLossCPerHour: 4,
    boilOffRateLPerHour: 3.5,
    kettleDeadspaceL: 1.5,
    chillerLossL: 0.3,
    fermenterTrubLossL: 1.5,
    mashEfficiencyPercent: 72,
    brewhouseEfficiencyPercent: 66,
    kettleVolumeL: 35,
    mashTunVolumeL: 35,
  },
  {
    ...SHARED,
    id: 'generic-nano-1-bbl',
    name: 'Generic 1 bbl nano',
    maker: 'Generic',
    description:
      'A representative single-barrel nanobrewery: jacketed stainless mash tun and kettle, a plate chiller, a pump between every vessel and a fly sparge. The smallest system that is a business rather than a hobby.',
    notes:
      'No single manufacturer published these figures — this is a stand-in for the class, sized on a 1 bbl (31 gal) transfer. Fixed losses are large in absolute gallons and small as a share of the batch, which is exactly the economy of scale.',
    nominalBatchVolumeL: 124,
    tags: ['nano', 'commercial', 'three-vessel', 'fly-sparge'],
    spargeType: 'fly',
    mashTunDeadspaceL: 0,
    mashTunLossL: 6.0,
    grainAbsorptionLPerKg: 1.0,
    mashThicknessLPerKg: 2.8,
    mashStandingLossCPerHour: 1,
    boilOffRateLPerHour: 8.0,
    kettleDeadspaceL: 6.0,
    chillerLossL: 3.0,
    fermenterTrubLossL: 8.0,
    mashEfficiencyPercent: 82,
    brewhouseEfficiencyPercent: 76,
    kettleVolumeL: 190,
    mashTunVolumeL: 170,
  },
];

/**
 * The other makers' condenser siblings, keyed by the open-boil row each one
 * shadows. Each is placed directly after its row, so the two read as a pair
 * wherever the shelf is listed.
 *
 * Boil-off, in litres an hour, and whose figure it is:
 *
 * | maker | their guidance | rule here |
 * | --- | --- | --- |
 * | Spike | "at least 50 % less boil-off" | half |
 * | Anvil / Blichmann | "start at 50 % power"; boil-off "around 5 %" | half, checked against 5 % of pre-boil |
 * | KegLand | none published | half (convention) |
 * | Brewtools | "much less power", no rate | half (convention) |
 * | Bräu Supply | "20–30 % less volume loss" | three quarters |
 * | Ruby Street | 4 → 1 gal/h at 25–35 % output on the 1 bbl | a quarter |
 *
 * Every one of these is the number to recalibrate first, and the notes say so:
 * nozzle, tap pressure and how far the element is turned down all move it.
 */
const CONDENSER_SIBLINGS: ReadonlyMap<string, CondenserSpec> = new Map<string, CondenserSpec>([
  // ---- Anvil: the Foundry Steam Condenser, on the lid tube kit. ----
  [
    'anvil-foundry-6-5',
    {
      id: 'anvil-foundry-6-5-steam-condenser',
      name: 'Anvil Foundry 6.5 gal + Steam Condenser',
      description:
        "The 6.5 gal Foundry with Anvil's own Steam Condenser on the lid tube kit. Same kettle, malt pipe and 5 gallon batch; the lid is sealed, the element runs at half power or less, and the steam leaves as warm water down a drain.",
      notes:
        "Anvil's manual says to start at half the power of an open boil and that boil-off lands around 5 % — on a 6 gallon pre-boil that is about 0.3 gal/h, and 1.2 L/h is that figure. It is less than half the open row's rate only because that row already assumes the lid mostly on. The condenser drinks about 9.5 gal/h of tap water at 40 psi; keep the exhaust at 120–130 °F by turning the element down, not the water up.",
      boilOffRateLPerHour: 1.2,
    },
  ],
  [
    'anvil-foundry-10-5',
    {
      id: 'anvil-foundry-10-5-steam-condenser',
      name: 'Anvil Foundry 10.5 gal + Steam Condenser',
      description:
        "The 10.5 gal Foundry with Anvil's Steam Condenser. Same kettle, malt pipe and batch range as the open row; the boil runs sealed at about half power.",
      notes:
        'Half of the open row\'s 0.75 gal/h is 0.37 gal/h, which is also what Anvil\'s "around 5 %" comes to on a 7.5 gallon pre-boil, so the two rules agree here. Start the element at 50 % and turn it down until the exhaust water sits at 120–130 °F. Measure the first batch — the nozzle Anvil ships is sized for 5 and 10 gallon batches and a different one moves this.',
      boilOffRateLPerHour: 1.4,
    },
  ],
  [
    'anvil-foundry-18',
    {
      id: 'anvil-foundry-18-steam-condenser',
      name: 'Anvil Foundry 18 gal + Steam Condenser',
      description:
        "The 18 gal Foundry with Anvil's Steam Condenser. The open row is the one Anvil tell you to boil uncovered; this is the way to run it indoors, lid sealed and the element turned well down.",
      notes:
        "Half the open row's 1.2 gal/h is 0.6 gal/h, and Anvil's 5 % on a 12 gallon pre-boil is the same 0.6. Anvil's supplied nozzle is rated for 5 and 10 gallon batches; on a double batch they suggest a larger one, and a larger nozzle at the same power moves this number down, so recalibrate after the first brew.",
      boilOffRateLPerHour: 2.25,
    },
  ],
  // ---- KegLand: the 2" condenser set on the 35 and 65, the 4" TC condenser lid on the 65 and 100. ----
  [
    'brewzilla-gen4-35',
    {
      id: 'brewzilla-gen4-35-steam-condenser',
      name: 'BrewZilla Gen 4 35 L + steam condenser',
      description:
        'The 35 L BrewZilla with KegLand\'s 2" tri-clover steam condenser set on the lid. Same kettle, pump and 23 L batch; the boil runs sealed at reduced power.',
      notes:
        'KegLand publish no boil-off for the condenser, so this row carries the half-rate convention every other maker lands on — 0.4 gal/h against 0.8 open — and it is the first number to recalibrate. The Gen 4 controller lets you set boil power directly; a wide 35 L body needs less than you think to keep rolling with the lid sealed.',
      boilOffRateLPerHour: 1.5,
    },
  ],
  [
    'brewzilla-gen4-65',
    {
      id: 'brewzilla-gen4-65-steam-condenser',
      name: 'BrewZilla Gen 4 65 L + steam condenser',
      description:
        'The 65 L BrewZilla under KegLand\'s Pro steam condenser lid — the 4" tri-clover lid with the 3" sight-glass port you drop hops through without lifting it. Same kettle and 45 L batch as the open row.',
      notes:
        'Half of the open row: KegLand publish no figure, and half is what Spike, Anvil and Brew Hardware all quote for the same arrangement. Recalibrate first. The sight-glass port means hop additions do not break the seal, so the rate is steadier through the boil than on a lid you keep lifting.',
      boilOffRateLPerHour: 2.25,
    },
  ],
  [
    'brewzilla-gen4-100',
    {
      id: 'brewzilla-gen4-100-steam-condenser',
      name: 'BrewZilla Gen 4 100 L + steam condenser',
      description:
        'The 100 L BrewZilla under KegLand\'s Pro steam condenser lid, the same 4" tri-clover lid as the 65 in the larger bore. Same kettle and 75 L batch as the open row.',
      notes:
        'The open row derives 6 L/h from 6.6 kW across a 52 cm bore; sealed and condensed, the convention is half. A single 2" condenser nozzle is marginal at this size — KegLand sell the lid for the 4" port for a reason — and an undersized spray lets steam past, which shows up as a rate nearer the open row. Recalibrate first.',
      boilOffRateLPerHour: 3.0,
    },
  ],
  // ---- Brewtools: their Steam Condenser on the 4" TC steam hat. ----
  [
    'brewtools-b40pro',
    {
      id: 'brewtools-b40pro-steam-condenser',
      name: 'Brewtools B40pro + Steam Condenser',
      description:
        'The B40pro with Brewtools\' own Steam Condenser clamped to the 4" steam hat. Same tank, malt pipe and 30 L batch; the boil runs sealed with the element turned down.',
      notes:
        'Brewtools publish no rate for the condenser, only that you need much less power to keep the boil going and should turn it down before the wort climbs. Half of the derived 3.5 L/h is the convention here. Their nozzle passes 1.1 L/min at 4 bar and the B40pro needs less cooling than the 6 kW machines; the smallest nozzle saves water and, run too small, lets steam past. Recalibrate first.',
      boilOffRateLPerHour: 1.75,
    },
  ],
  [
    'brewtools-b80pro',
    {
      id: 'brewtools-b80pro-steam-condenser',
      name: 'Brewtools B80pro + Steam Condenser',
      description:
        "The B80pro with Brewtools' Steam Condenser on the steam hat. Same 90 L tank and 60 L batch as the open row.",
      notes:
        'Half the open row, by convention; Brewtools publish no figure. At 6 kW this is the size they mean when they say the condenser needs its full nozzle, and the drain must run free to a bucket or floor drain — a submerged tube kills the vacuum and the steam comes out of the hat instead. Recalibrate boil-off first.',
      boilOffRateLPerHour: 2.75,
    },
  ],
  [
    'brewtools-b150pro',
    {
      id: 'brewtools-b150pro-steam-condenser',
      name: 'Brewtools B150pro + Steam Condenser',
      description:
        "The B150pro with Brewtools' Steam Condenser. Same 169 L tank and 120 L batch as the open row, boiled sealed at a fraction of 6.6 kW.",
      notes:
        'Half the open row, by convention. The open row already notes that 6.6 kW over a 60 cm bore barely out-evaporates the B80pro; sealed, the bore matters less and the number tracks power almost alone, so how far you turn the element down is the whole story. Recalibrate first, and size the nozzle for 6 kW or more.',
      boilOffRateLPerHour: 3.0,
    },
  ],
  // ---- Spike: the Steam Condensing Lid, sold for the 10, 15, 20, 30 and 50 gal kettles. ----
  [
    'spike-solo-10',
    {
      id: 'spike-solo-10-steam-condenser',
      name: 'Spike Solo 10 gal + Steam Condensing Lid',
      description:
        "The 10 gal Solo under Spike's Steam Condensing Lid, the sealed lid with the misting condenser built into its outlet. Same kettle, basket and batch range; the boil runs at reduced power and the room stays dry.",
      notes:
        "Spike's own claim is at least 50 % less boil-off, so this is half the open row: 0.4 gal/h against 0.8. The open row already straddles the 120 V and 240 V elements, and so does this one — a 1800 W boil under the lid loses less than 0.4, a 6000 W boil turned down to a simmer about that. Spike's own testing had DMS below threshold after 30 minutes sealed, so a shorter boil is on the table if you measure the gravity.",
      boilOffRateLPerHour: 1.5,
    },
  ],
  [
    'spike-solo-15',
    {
      id: 'spike-solo-15-steam-condenser',
      name: 'Spike Solo 15 gal + Steam Condensing Lid',
      description:
        "The 15 gal Solo under Spike's Steam Condensing Lid. Same 15 gal kettle and 6 gallon batch as the open row; 5500 W turned down and the lid sealed.",
      notes:
        "Half the open row's 1.05 gal/h, on Spike's stated 50 % — 0.5 gal/h is also where 5500 W kettle owners with any lid-on condenser tend to land at 30 % power. The lid fits the Spike kettle's rim; the discharge is near-boiling water, which Spike suggest keeping for cleaning. Recalibrate after the first batch.",
      boilOffRateLPerHour: 2.0,
    },
  ],
  [
    'spike-solo-20',
    {
      id: 'spike-solo-20-steam-condenser',
      name: 'Spike Solo 20 gal + Steam Condensing Lid',
      description:
        "The 20 gal Solo under Spike's Steam Condensing Lid, in the 20 gal size. Same wide kettle and 10 gallon batch as the open row.",
      notes:
        "Half of 1.3 gal/h is 0.65, on Spike's 50 %. The open row notes the 20 evaporates faster than the 15 because of surface, not power; sealed, surface stops mattering and this row and the 15 come closer together than their open siblings. Recalibrate first.",
      boilOffRateLPerHour: 2.5,
    },
  ],
  [
    'spike-trio-herms-10',
    {
      id: 'spike-trio-herms-10-steam-condenser',
      name: 'Spike Trio 10 gal electric HERMS + Steam Condensing Lid',
      description:
        'The Spike+ kettle HERMS with the Steam Condensing Lid on the boil kettle. Mash tun, HLT, coil and fly sparge are unchanged; only the kettle boil is sealed and condensed.',
      notes:
        "Half the open row, on Spike's 50 %. The lid goes on the boil kettle only — the HLT never boils and the mash tun is not in the picture — so mash efficiency and every loss figure are the open row's. Recalibrate boil-off first, as on every condenser row.",
      boilOffRateLPerHour: 2.0,
    },
  ],
  // ---- Bräu Supply: their Steam Condenser on the upper 1.5" TC port or the domed clamping lid. ----
  [
    'brau-supply-unibrau-10',
    {
      id: 'brau-supply-unibrau-10-steam-condenser',
      name: 'Bräu Supply Unibräu 10G + Steam Condenser',
      description:
        "The Unibräu 10G with Bräu Supply's Steam Condenser on the upper tri-clamp port or the domed clamping lid. Same kettle, basket and 6 gallon batch; the boil is throttled on the controller and the steam condensed.",
      notes:
        "Bräu Supply are the one maker who do not say half: they quote 20–30 % less volume loss with the condenser, depending on boil intensity and water flow, so this row is three quarters of the open row's 2.3 L/h. The open figure is already their throttled-EzBoil number, which is why the drop is smaller than on a kettle that starts from a full-power boil. Recalibrate first.",
      boilOffRateLPerHour: 1.7,
    },
  ],
  [
    'brau-supply-unibrau-20',
    {
      id: 'brau-supply-unibrau-20-steam-condenser',
      name: 'Bräu Supply Unibräu 20G + Steam Condenser',
      description:
        "The Unibräu 20G with Bräu Supply's Steam Condenser on the domed clamping lid. Same 20 gal kettle and 10 gallon batch as the open row.",
      notes:
        "Three quarters of the open row, on Bräu Supply's own 20–30 %: their published 10 %/h evaporation becomes about 7.5 %/h, which on this rig's pre-boil is 3.4 L/h. They advise easing the boil on the controller to a controlled roll rather than a specific power. Recalibrate first.",
      boilOffRateLPerHour: 3.4,
    },
  ],
  [
    'brau-supply-braupro-10',
    {
      id: 'brau-supply-braupro-10-steam-condenser',
      name: 'Bräu Supply BräuPro 10G + Steam Condenser',
      description:
        "The BräuPro 10G with Bräu Supply's Steam Condenser on the conical's upper port — they sell it for the BräuPro vessel specifically. The mash tun, the boil-in-the-fermenter and the cone loss are all the open row's.",
      notes:
        "Three quarters of the open row, on Bräu Supply's 20–30 %. Boiling in a conical under a condenser is exactly what the unit was drawn for: the vessel already seals. Nothing after the boil changes — no hot transfer, so still no kettle deadspace and no chiller loss. Recalibrate boil-off first.",
      boilOffRateLPerHour: 2.6,
    },
  ],
  [
    'brau-supply-braupro-20',
    {
      id: 'brau-supply-braupro-20-steam-condenser',
      name: 'Bräu Supply BräuPro 20G + Steam Condenser',
      description:
        "The BräuPro 20G with Bräu Supply's Steam Condenser on the unitank. Same 100 L vessel and 57 L batch as the open row.",
      notes:
        "Three quarters of the open row's 3.8 L/h, on Bräu Supply's 20–30 %. The open row notes the element is the limit on this rig, not the surface; that stays true sealed, so the drop is the throttle, not the lid. Recalibrate first.",
      boilOffRateLPerHour: 2.9,
    },
  ],
  // ---- Blichmann: the same unit as Anvil's, sold under both names, plus the 4" and 6" Pro condensers. ----
  [
    'blichmann-herms-10',
    {
      id: 'blichmann-herms-10-steam-condenser',
      name: 'Blichmann 10 gal electric HERMS + Steam Condenser',
      description:
        "The Blichmann electric HERMS with their Steam Condenser on the 20 gal boil kettle's lid. Mash tun, HLT, coil, fly sparge and Therminator are unchanged; only the boil is sealed.",
      notes:
        "Blichmann's condenser is the same unit Anvil sell, with the same instruction: start at half the power of an open boil, and hold the exhaust water at 120–130 °F. Half of the open row's 1.2 gal/h is 0.6. A 20 gal kettle on a 10 gallon batch has headspace to spare, which a sealed boil does not need but does not mind. Recalibrate first.",
      boilOffRateLPerHour: 2.25,
    },
  ],
  [
    'blichmann-breweasy-5',
    {
      id: 'blichmann-breweasy-5-steam-condenser',
      name: 'Blichmann BrewEasy 5 gal + Steam Condenser',
      description:
        "The 5 gal BrewEasy with Blichmann's Steam Condenser on the boil kettle. The mash tun still sits over the kettle and drains into it; the kettle's boil is sealed.",
      notes:
        "Half the open row, on Blichmann's half-power instruction — 0.37 gal/h against 0.75. The condenser needs a tri-clamp lid port, which the BoilerMaker lids take through the lid tube kit; without it there is nothing to clamp to. Recalibrate first.",
      boilOffRateLPerHour: 1.4,
    },
  ],
  [
    'blichmann-breweasy-10',
    {
      id: 'blichmann-breweasy-10-steam-condenser',
      name: 'Blichmann BrewEasy 10 gal + Steam Condenser',
      description:
        "The 10 gal BrewEasy pairing with Blichmann's Steam Condenser on the boil kettle. Same two kettles and 10 gallon batch as the open row.",
      notes:
        'Half of the open row\'s derived 1.2 gal/h. Blichmann rate the condenser for lids up to 16.5" across with a tri-clamp port; the 20 gal BoilerMaker on this pairing is inside that. Start at 50 % power and turn down until the exhaust runs 120–130 °F. Recalibrate first.',
      boilOffRateLPerHour: 2.25,
    },
  ],
  [
    'blichmann-breweasy-20',
    {
      id: 'blichmann-breweasy-20-steam-condenser',
      name: 'Blichmann BrewEasy 20 gal + Steam Condenser',
      description:
        'The 20 gal BrewEasy pairing with a Blichmann Steam Condenser on the boil kettle. Same two kettles and 20 gallon batch as the open row.',
      notes:
        'Half of the open row\'s 1.3 gal/h. This pairing\'s 30 gal boil kettle is past the 16.5" lid the standard condenser is rated for, so it is the 4" Pro condenser or a lid adapted for it; either way the half-power rule holds. Recalibrate first.',
      boilOffRateLPerHour: 2.5,
    },
  ],
  // ---- Ruby Street: their Steam Condenser, measured on the 1 bbl. ----
  [
    'ruby-street-fusion-15-gas',
    {
      id: 'ruby-street-fusion-15-gas-steam-condenser',
      name: 'Ruby Street Fusion 15 gas + Steam Condenser',
      description:
        "The Fusion 15 with Ruby Street's Steam Condenser on the boil kettle, burner turned well down and the lid closed. Direct-fired mash tun, HLT and fly sparge are unchanged.",
      notes:
        "Ruby Street measured their condenser on the 1 bbl: 4 gal/h open at 60–75 % output became 1 gal/h sealed at 25–35 %, and above 40 % the unit is overwhelmed. That is a quarter, and a quarter of this row's open 4 L/h is 1 L/h. It is a gas burner, so the throttle is the valve, and the unit uses about 9 gal of tap water an hour. Recalibrate first; the open row's rate is itself scaled by bore, not measured.",
      boilOffRateLPerHour: 1.0,
    },
  ],
  [
    'ruby-street-alpha-ruby-1-bbl-gas',
    {
      id: 'ruby-street-alpha-ruby-1-bbl-gas-steam-condenser',
      name: 'Ruby Street Alpha Ruby 1 bbl gas + Steam Condenser',
      description:
        "The Alpha Ruby with Ruby Street's Steam Condenser on the 50 gal boil kettle — the rig the unit was tested on. Three direct-fired kettles as in the open row; the boil is lidded and the burner held at a quarter to a third of its output.",
      notes:
        "Ruby Street's own test on this system: 35 gallons pre-boil, 4 gal/h open, averaging 1 gal/h with the condenser at 25–35 % burner output. The open row here carries 2.4 gal/h rather than their 4 because it is scaled from the propane cooler row, so the same quarter is applied to it — 0.6 gal/h — instead of copying their absolute. Keep the waste water at 130–150 °F, and recalibrate first.",
      boilOffRateLPerHour: 2.25,
    },
  ],
]);

/**
 * The shelf, with each condenser sibling placed directly after its open row.
 */
export const equipmentPresets: readonly EquipmentPreset[] = SHELF.flatMap((preset) => {
  const spec = CONDENSER_SIBLINGS.get(preset.id);
  return spec === undefined ? [preset] : [preset, withCondenser(preset, spec)];
});
